import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  TableCellsIcon,
  UserGroupIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  LinkIcon,
  UserIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  EyeIcon,
  // TagIcon,
  SparklesIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ClockIcon,
  TrashIcon,
  ShoppingCartIcon,
  FireIcon,
  QrCodeIcon,
  CogIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline'
import jsQR from 'jsqr'
import { useCategories } from '../contexts/CategoryContext'
import { useAuth } from '../contexts/AuthContext'
import { apiService, resolveImageUrl, formatItemName } from '../services/api'
import NumericKeypad from '../components/UI/NumericKeypad'
import AllergenIcons, { type AllergenInfo } from '../components/UI/AllergenIcons'
import { compareProductCode } from '../utils/productCodeSort'
import { splitVatInclusive } from '../utils/tax'

interface Table {
  id: string
  number: string
  capacity: number
  status: 'available' | 'occupied' | 'reserved' | 'cleaning' | 'waiting_for_approval'
  location: 'indoor' | 'outdoor' | 'bar'
  floor_plan_id?: string
  currentOrder?: Order
  orders?: Order[]
  reservation?: Reservation
  customerCount?: number
  additionalChairs?: number
  mergedTables?: string[]
  updated_at?: string
}

interface Order {
  id: string
  orderNumber: string
  tableId: string
  customerName?: string
  customerPhone?: string
  customerId?: string
  status: string
  approvalStatus?: string
  total: number
  subtotal?: number
  taxAmount?: number
  serviceCharge?: number
  discountAmount?: number
  items: OrderItem[]
  /** Per-line items for modification modal (no KDS quantity summing); falls back to items if absent */
  itemsForModification?: OrderItem[]
  createdAt: string
  modificationRequest?: ModificationRequest
  cancellationRequest?: CancellationRequest
  pendingAdditionalItems?: any[]
  pendingAdditionalItemsStatus?: string
  paymentMethod?: string
  paymentStatus?: string
}

interface ModificationRequest {
  id: string
  modificationType: string
  requestedChanges: any
  reason?: string
  approvalStatus: string
  requestedAt: string
}

interface CancellationRequest {
  id: string
  cancellationReason: string
  reasonDetails?: string
  approvalStatus: string
  requestedAt: string
}

interface OrderItem {
  id: string
  itemName: string
  quantity: number
  unitPrice: number
  totalPrice: number
  status: string
  priority?: number
  /** From order_items.menu_item_id — required for accurate void/KDS diff when catalog name differs from DB */
  menuItemId?: string
  itemCode?: string
}

interface Reservation {
  id: string
  customerName: string
  customerPhone: string
  partySize: number
  reservationTime: string
  status: string
}

interface Product {
  id: string
  name: string
  basePrice: number
  categoryId: string
  code?: string
  image?: string
  description?: string
  isActive: boolean
  calories?: number
  stepsToBurn?: number
  allergens?: string[]
  variants?: MenuVariant[]
  addons?: Addon[]
}

interface MenuVariant {
  id: string
  name: string
  priceModifier: number
  isDefault: boolean
  allergen_ids?: string[]
}

interface Addon {
  id: string
  name: string
  price: number
  category: string
  isRequired: boolean
  allergen_ids?: string[]
}

interface CartItem {
  id: string
  name: string
  basePrice: number
  finalPrice: number
  product: Product
  quantity: number
  notes?: string
  selectedVariant?: MenuVariant
  selectedAddons?: Addon[]
  priority: number
  categoryId: string
  categoryPriority: number
  /** order_items.item_name — send on modification so backend void diff matches DB rows */
  orderItemNameDb?: string
  /** order_items.menu_item_id when catalog match failed but DB has id */
  sourceMenuItemId?: string
  isComboItem?: boolean
  comboPrice?: number
  originalPrice?: number
  isBOGOItem?: boolean
  bogoDiscountType?: 'free' | 'percentage' | 'fixed'
  bogoDiscountValue?: number
  paidQuantity?: number
  freeQuantity?: number
  isFreeItem?: boolean
  discountOfferId?: string
  discountOriginalPrice?: number
  discountType?: 'percentage' | 'fixed'
  discountValue?: number
}

interface Customer {
  id: string
  name?: string
  mobile_number?: string
  phone?: string
  first_name?: string
  last_name?: string
  visit_count?: number
}

interface FloorPlan {
  id: string
  name: string
  level: number
}

interface Offer {
  id: string
  name: string
  description: string
  type: 'percentage' | 'fixed' | 'bogo' | 'combo'
  value: number
  isActive: boolean
  applicableItems: string[]
  minOrderAmount?: number
  maxDiscount?: number
  comboItems?: any[]
  bogoItems?: any[]
}

/** Payload from customer order QR (matches customer-frontend OrderQRSuccess). */
interface OrderQRPayload {
  v: number
  cn?: string
  cm?: string
  n?: string
  i: Array<{
    mi: string
    na: string
    q: number
    vn?: string
    vpm?: number
    ad?: string[]
    ap?: number
    bp: number
    no?: string
  }>
}

function sortAddonIdsForMerge(addons?: Addon[]): string {
  return (addons || []).map((a) => a.id).sort().join(',')
}

/** Same product line: merge qty unless variant, addons, or notes differ; skip order-modification rows */
function waiterCartItemsMergeable(a: CartItem, b: CartItem): boolean {
  if (a.product.id !== b.product.id) return false
  if ((a.selectedVariant?.id || '') !== (b.selectedVariant?.id || '')) return false
  if (sortAddonIdsForMerge(a.selectedAddons) !== sortAddonIdsForMerge(b.selectedAddons)) return false
  if ((a.notes || '').trim() !== (b.notes || '').trim()) return false
  if (a.orderItemNameDb || b.orderItemNameDb) return false
  if (a.sourceMenuItemId || b.sourceMenuItemId) return false
  if ((a.discountOfferId || '') !== (b.discountOfferId || '')) return false
  if (!!a.isComboItem !== !!b.isComboItem) return false
  if (!!a.isBOGOItem !== !!b.isBOGOItem) return false
  if (!!a.isFreeItem !== !!b.isFreeItem) return false
  return true
}

const WaiterModule: React.FC = () => {
  const { categories, getAllCategoriesFlat } = useCategories()
  const { user } = useAuth()
  const [tables, setTables] = useState<Table[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [allergenMap, setAllergenMap] = useState<Record<string, AllergenInfo>>({})
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([])
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<string>('all')
  const [cart, setCart] = useState<CartItem[]>([])
  const [tableCarts, setTableCarts] = useState<Record<string, CartItem[]>>({})
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all')
  const [productSearchTerm, setProductSearchTerm] = useState<string>('')
  const [showCustomizationModal, setShowCustomizationModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<MenuVariant | null>(null)
  const [selectedAddons, setSelectedAddons] = useState<Addon[]>([])
  const [itemNotes, setItemNotes] = useState('')
  const [orderNotes, setOrderNotes] = useState('')
  const [loading, setLoading] = useState(true)
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [restaurantSettings, setRestaurantSettings] = useState<any>({})
  const [posSettings, setPosSettings] = useState<any>({})
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [_registerStatus, setRegisterStatus] = useState<{ isOpen: boolean } | null>(null)
  
  // New state for enhanced features
  const [customerMobile, setCustomerMobile] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false)
  const [showMobileKeypad, setShowMobileKeypad] = useState(false)
  const [customerVisitCount, setCustomerVisitCount] = useState<number | null>(null)
  const [customerCount, setCustomerCount] = useState<number>(0)
  const [showTableSettingsModal, setShowTableSettingsModal] = useState(false)
  const [isRefreshingTable, setIsRefreshingTable] = useState(false)
  const [lastTableId, setLastTableId] = useState<string | null>(null)
  const [lastRefreshAttempt, setLastRefreshAttempt] = useState<number>(0)
  const [refreshAttemptCount, setRefreshAttemptCount] = useState<number>(0)
  const [pendingPlaceOrder, setPendingPlaceOrder] = useState(false)
  const [showMergeTablesModal, setShowMergeTablesModal] = useState(false)
  const [showTransferOrderModal, setShowTransferOrderModal] = useState(false)
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false)
  const [tablesToMerge, setTablesToMerge] = useState<string[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    message: string
    onConfirm: () => void
    confirmButtonText?: string
    confirmButtonColor?: 'red' | 'green' | 'blue'
  } | null>(null)
  const [additionalChairs, setAdditionalChairs] = useState<number>(0)
  const [tableSearchTerm, setTableSearchTerm] = useState('')
  const [currentTime, setCurrentTime] = useState(new Date())
  const [targetTableId, setTargetTableId] = useState<string>('')
  const [isTransferringOrder, setIsTransferringOrder] = useState(false)
  const [transferModalFloorPlanId, setTransferModalFloorPlanId] = useState<string>('all')
  const [transferTableSearchTerm, setTransferTableSearchTerm] = useState('')
  const [showOffersModal, setShowOffersModal] = useState(false)
  const [availableOffers, setAvailableOffers] = useState<Offer[]>([])
  const [appliedOffers, setAppliedOffers] = useState<Offer[]>([])
  const [showModificationRequestModal, setShowModificationRequestModal] = useState(false)
  const [showCancellationRequestModal, setShowCancellationRequestModal] = useState(false)
  const [modificationReason, setModificationReason] = useState('')
  const [cancellationReason, setCancellationReason] = useState('')
  const [cancellationReasonDetails, setCancellationReasonDetails] = useState('')
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const [isRequestingBill, setIsRequestingBill] = useState(false)
  const [hasPendingBillRequest, setHasPendingBillRequest] = useState(false)
  const [lastOrderToken, setLastOrderToken] = useState<number | null>(null)
  const [showScanQRModal, setShowScanQRModal] = useState(false)
  const [cartFooterExpanded, setCartFooterExpanded] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const fullscreenRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = () => setIsFullScreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const toggleFullScreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
      } else if (fullscreenRef.current) {
        await fullscreenRef.current.requestFullscreen()
      }
    } catch (err) {
      console.warn('Fullscreen error:', err)
    }
  }
  const [scanQRError, setScanQRError] = useState<string | null>(null)
  const scanQRInputRef = useRef<HTMLInputElement>(null)
  const scanQRVideoRef = useRef<HTMLVideoElement>(null)
  const scanQRStreamRef = useRef<MediaStream | null>(null)
  const scanQRRafRef = useRef<number | null>(null)

  // Live camera QR scan when modal is open
  useEffect(() => {
    if (!showScanQRModal) {
      if (scanQRStreamRef.current) {
        scanQRStreamRef.current.getTracks().forEach(t => t.stop())
        scanQRStreamRef.current = null
      }
      if (scanQRRafRef.current != null) {
        cancelAnimationFrame(scanQRRafRef.current)
        scanQRRafRef.current = null
      }
      return
    }
    setScanQRError(null)
    const video = scanQRVideoRef.current
    if (!video) return

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      setScanQRError('Could not start camera')
      return
    }

    const startScanLoop = (stream: MediaStream) => {
      scanQRStreamRef.current = stream
      video.srcObject = stream
      video.play().then(() => {
        const tick = () => {
          if (!scanQRStreamRef.current || !video.srcObject) return
          if (video.readyState >= 2) {
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            ctx.drawImage(video, 0, 0)
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            const result = jsQR(imageData.data, imageData.width, imageData.height)
            if (result?.data) {
              try {
                const payload = JSON.parse(result.data) as OrderQRPayload
                if (payload.v === 1 && Array.isArray(payload.i) && payload.i.length > 0) {
                  loadCartFromQRPayload(payload)
                  setShowScanQRModal(false)
                  setToast({ message: 'Order loaded from QR', type: 'success' })
                  return
                }
              } catch {
                // ignore invalid JSON
              }
            }
          }
          scanQRRafRef.current = requestAnimationFrame(tick)
        }
        scanQRRafRef.current = requestAnimationFrame(tick)
      })
    }

    const tryCamera = (constraints: MediaStreamConstraints) =>
      navigator.mediaDevices.getUserMedia(constraints).then(startScanLoop)

    tryCamera({ video: { facingMode: 'environment' } })
      .catch(() => tryCamera({ video: true }))
      .catch(() => {
        setScanQRError('Camera access denied or not available')
      })

    return () => {
      if (scanQRStreamRef.current) {
        scanQRStreamRef.current.getTracks().forEach(t => t.stop())
        scanQRStreamRef.current = null
      }
      if (scanQRRafRef.current != null) {
        cancelAnimationFrame(scanQRRafRef.current)
        scanQRRafRef.current = null
      }
    }
  }, [showScanQRModal])

  useEffect(() => {
    loadData()
  }, [])

  // Fix 5: Waiter Module - Listen for Table Clear Events
  useEffect(() => {
    const handleTableCleared = async (event: CustomEvent) => {
      console.log('🔄 [Waiter Module] Table cleared event received, refreshing...', event.detail)
      
      const { tableId, tableNumber, tableCleared, tableVerified } = event.detail || {}
      
      if (tableCleared && tableVerified) {
        // Refresh the specific table if tableId is provided
        if (tableId) {
          console.log(`🔄 [Waiter Module] Refreshing table ${tableId} (${tableNumber || 'unknown'}) after clearing`)
          // Fetch single table to update its status
          try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
            const response = await fetch(`${apiUrl}/tables/${tableId}`)
            const data = await response.json()
            
            if (data.success && data.data) {
              const updatedTable = data.data
              // Update the table in the tables array
              setTables(prevTables => {
                const updatedTables = prevTables.map(t => 
                  t.id === tableId ? { 
                    ...t, 
                    ...updatedTable, 
                    status: updatedTable.status || 'available',
                    // Show order only if approved (not draft/waiting_for_approval) or completed on occupied table
                    currentOrder: updatedTable.current_order && 
                      ((updatedTable.current_order.status !== 'draft' && 
                        updatedTable.current_order.status !== 'waiting_for_approval') ||
                       (updatedTable.current_order.status === 'completed' && 
                        (updatedTable.status === 'occupied' || updatedTable.status === 'waiting_for_approval'))) 
                      ? updatedTable.current_order : undefined
                  } : t
                )
                // If table not found in array, add it
                if (!updatedTables.find(t => t.id === tableId)) {
                  return [...updatedTables, { 
                    ...updatedTable, 
                    status: updatedTable.status || 'available' 
                  }]
                }
                return updatedTables
              })
              
              // If this table is currently selected, update selectedTable
              if (selectedTable?.id === tableId) {
                setSelectedTable(prev => prev ? { 
                  ...prev, 
                  status: updatedTable.status || 'available', 
                  // Show order only if approved (not draft/waiting_for_approval) or completed on occupied table
                  currentOrder: updatedTable.current_order && 
                    ((updatedTable.current_order.status !== 'draft' && 
                      updatedTable.current_order.status !== 'waiting_for_approval') ||
                     (updatedTable.current_order.status === 'completed' && 
                      (updatedTable.status === 'occupied' || updatedTable.status === 'waiting_for_approval'))) 
                    ? updatedTable.current_order : undefined 
                } : null)
              }
              
              console.log(`✅ [Waiter Module] Table ${tableId} refreshed successfully:`, {
                tableId: tableId,
                tableNumber: updatedTable.number,
                status: updatedTable.status,
                hasCurrentOrder: !!updatedTable.current_order
              })
            }
          } catch (error) {
            console.error('❌ [Waiter Module] Error refreshing table:', error)
            // Fallback: refresh all tables
            window.dispatchEvent(new CustomEvent('refreshTables'))
          }
        } else {
          // Refresh all tables if no specific tableId
          console.log('🔄 [Waiter Module] Refreshing all tables after clearing')
          window.dispatchEvent(new CustomEvent('refreshTables'))
        }
      } else {
        console.warn('⚠️ [Waiter Module] Table cleared event received but clearing was not successful:', event.detail)
      }
    }
    
    window.addEventListener('tableCleared', handleTableCleared as unknown as EventListener)
    return () => window.removeEventListener('tableCleared', handleTableCleared as unknown as EventListener)
  }, [selectedTable]) // Only include selectedTable to avoid recreating listener

  // Listen for table refresh events
  useEffect(() => {
    const handleRefreshTables = () => {
      console.log('🔄 [Waiter Module] Refresh tables event received')
      loadData()
    }
    
    window.addEventListener('refreshTables', handleRefreshTables)
    return () => window.removeEventListener('refreshTables', handleRefreshTables)
  }, [])

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Close keypad when clicking outside
  const keypadRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (keypadRef.current && !keypadRef.current.contains(event.target as Node)) {
        // Check if the click is not on the input field
        const target = event.target as HTMLElement
        if (!target.closest('input[type="tel"]')) {
          setShowMobileKeypad(false)
        }
      }
    }

    if (showMobileKeypad) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showMobileKeypad])

  useEffect(() => {
    if (selectedTable) {
      // Only update customerCount and additionalChairs if:
      // 1. This is a different table (table ID changed), OR
      // 2. The table settings modal is NOT open (user is not editing)
      const isDifferentTable = lastTableId !== selectedTable.id
      const shouldUpdateCounts = isDifferentTable || !showTableSettingsModal
      
      if (shouldUpdateCounts) {
        setCustomerCount(selectedTable.customerCount || 0)
        setAdditionalChairs(selectedTable.additionalChairs || 0)
      }
      
      // Update lastTableId to track table changes
      if (isDifferentTable) {
        setLastTableId(selectedTable.id)
        setRefreshAttemptCount(0) // Reset refresh attempts when switching tables
      }
      
      // Debug: Log selected table and order info
      // Commented out to reduce console noise - uncomment for debugging if needed
      // console.log('Selected table:', {
      //   tableId: selectedTable.id,
      //   tableNumber: selectedTable.number,
      //   hasCurrentOrder: !!selectedTable.currentOrder,
      //   currentOrderId: selectedTable.currentOrder?.id,
      //   currentOrderStatus: selectedTable.currentOrder?.status,
      //   currentOrderStatusRaw: selectedTable.currentOrder?.status,
      //   orderNumber: selectedTable.currentOrder?.orderNumber,
      //   tableStatus: selectedTable.status,
      //   orderItemsCount: selectedTable.currentOrder?.items?.length || 0,
      //   isRefreshing: isRefreshingTable,
      //   isModalOpen: showTableSettingsModal
      // })
      
      if (selectedTable.currentOrder) {
        setCustomerMobile(selectedTable.currentOrder.customerPhone || '')
        if (selectedTable.currentOrder.customerId) {
          loadCustomerById(selectedTable.currentOrder.customerId)
        }
        // Check for existing bill request
        checkBillRequestStatus(selectedTable.currentOrder.id)
      } else {
        setHasPendingBillRequest(false)
        // If no currentOrder but table should have one, try to refresh the single table
        // Don't call loadTables() as it can cause 500 errors - fetch just this table instead
        // Only refresh if not already refreshing, modal is not open, and we haven't refreshed too recently
        const now = Date.now()
        const timeSinceLastRefresh = now - lastRefreshAttempt
        const REFRESH_COOLDOWN = 5000 // 5 seconds cooldown between refresh attempts
        const MAX_REFRESH_ATTEMPTS = 3 // Maximum number of refresh attempts before giving up
        
        if ((selectedTable.status === 'occupied' || selectedTable.status === 'waiting_for_approval') && 
            !isRefreshingTable && 
            !showTableSettingsModal &&
            timeSinceLastRefresh > REFRESH_COOLDOWN &&
            refreshAttemptCount < MAX_REFRESH_ATTEMPTS) {
          console.log('⚠️ [Frontend] Table is occupied but no currentOrder found, refreshing single table...', {
            tableId: selectedTable.id,
            tableNumber: selectedTable.number,
            tableStatus: selectedTable.status,
            refreshAttempt: refreshAttemptCount + 1,
            maxAttempts: MAX_REFRESH_ATTEMPTS
          })
          
          setIsRefreshingTable(true)
          setLastRefreshAttempt(now)
          setRefreshAttemptCount(prev => prev + 1)
          
          // Retry function for fetching table
          const fetchTableWithRetry = async (retryCount = 0): Promise<void> => {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
            const maxRetries = 1 // Retry once if first attempt fails
            
            try {
              const res = await fetch(`${apiUrl}/tables/${selectedTable.id}`)
              console.log('📡 [Frontend] Single table fetch response:', {
                status: res.status,
                ok: res.ok,
                tableId: selectedTable.id,
                attempt: retryCount + 1
              })
              
              if (!res.ok) {
                console.error('❌ [Frontend] Failed to fetch table:', res.status, res.statusText)
                if (retryCount < maxRetries) {
                  console.log(`🔄 [Frontend] Retrying table fetch (attempt ${retryCount + 2}/${maxRetries + 1})...`)
                  await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second before retry
                  return fetchTableWithRetry(retryCount + 1)
                }
                return
              }
              
              const data = await res.json()
              console.log('📦 [Frontend] Single table fetch data received:', {
                success: data?.success,
                hasData: !!data?.data,
                hasCurrentOrder: !!data?.data?.current_order,
                currentOrderId: data?.data?.current_order?.id,
                currentOrderStatus: data?.data?.current_order?.status,
                tableCurrentOrderId: data?.data?.current_order_id
              })
              
              if (data?.success && data.data) {
                const updatedTable = data.data
                
                // Log the raw data to see what we're getting
                console.log('🔍 [Frontend] Raw table data from backend:', {
                  tableId: updatedTable.id,
                  current_order_id: updatedTable.current_order_id,
                  hasCurrentOrder: !!updatedTable.current_order,
                  currentOrderStatus: updatedTable.current_order?.status,
                  currentOrderId: updatedTable.current_order?.id,
                  orderItemsCount: updatedTable.current_order?.order_items?.length || 0
                })
                
                // Transform and update selectedTable (same transformation as in loadTables)
                const transformedTable: Table = {
                  id: updatedTable.id,
                  number: updatedTable.number,
                  capacity: updatedTable.capacity,
                  status: updatedTable.status || 'available',
                  location: (updatedTable.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
                  floor_plan_id: updatedTable.floor_plan_id,
                  customerCount: updatedTable.customer_count || 0,
                  additionalChairs: updatedTable.additional_chairs || 0,
                  mergedTables: updatedTable.merged_tables || [],
                  // Show order only if approved (not draft/waiting_for_approval) or completed on occupied table
                  currentOrder: updatedTable.current_order && 
                    ((updatedTable.current_order.status !== 'draft' && 
                      updatedTable.current_order.status !== 'waiting_for_approval') ||
                     (updatedTable.current_order.status === 'completed' && 
                      (updatedTable.status === 'occupied' || updatedTable.status === 'waiting_for_approval'))) ? {
                    id: updatedTable.current_order.id,
                    orderNumber: updatedTable.current_order.order_number,
                    tableId: updatedTable.id,
                    customerName: updatedTable.current_order.customer_name,
                    customerPhone: updatedTable.current_order.customer_phone,
                    customerId: updatedTable.current_order.customer_id,
                    status: updatedTable.current_order.status,
                    approvalStatus: updatedTable.current_order.approval_status,
                    total: parseFloat(updatedTable.current_order.total || updatedTable.current_order.total_amount || 0),
                    subtotal: parseFloat(updatedTable.current_order.subtotal || 0),
                    taxAmount: parseFloat(updatedTable.current_order.tax_amount || 0),
                    serviceCharge: parseFloat(updatedTable.current_order.service_charge || 0),
                    discountAmount: parseFloat(updatedTable.current_order.discount_amount || 0),
                    items: (updatedTable.current_order.order_items || []).map((item: any) => ({
                      id: item.id,
                      itemName: item.item_name,
                      quantity: item.quantity,
                      unitPrice: parseFloat(item.unit_price || 0),
                      totalPrice: parseFloat(item.total_price || 0),
                      status: item.status || 'pending',
                      priority: item.priority,
                      menuItemId: item.menu_item_id || undefined,
                      itemCode: item.item_code || undefined
                    })),
                    itemsForModification: (updatedTable.current_order.order_items_for_modification || updatedTable.current_order.order_items || []).map((item: any) => ({
                      id: item.id,
                      itemName: item.item_name,
                      quantity: item.quantity,
                      unitPrice: parseFloat(item.unit_price || 0),
                      totalPrice: parseFloat(item.total_price || 0),
                      status: item.status || 'pending',
                      priority: item.priority,
                      menuItemId: item.menu_item_id || undefined,
                      itemCode: item.item_code || undefined
                    })),
                    createdAt: updatedTable.current_order.created_at,
                    modificationRequest: updatedTable.current_order.order_modification_requests?.[0] ? {
                      id: updatedTable.current_order.order_modification_requests[0].id,
                      modificationType: updatedTable.current_order.order_modification_requests[0].modification_type,
                      requestedChanges: updatedTable.current_order.order_modification_requests[0].requested_changes,
                      reason: updatedTable.current_order.order_modification_requests[0].reason,
                      approvalStatus: updatedTable.current_order.order_modification_requests[0].approval_status,
                      requestedAt: updatedTable.current_order.order_modification_requests[0].requested_at
                    } : undefined,
                    cancellationRequest: updatedTable.current_order.order_cancellation_requests?.[0] ? {
                      id: updatedTable.current_order.order_cancellation_requests[0].id,
                      cancellationReason: updatedTable.current_order.order_cancellation_requests[0].cancellation_reason,
                      reasonDetails: updatedTable.current_order.order_cancellation_requests[0].reason_details,
                      approvalStatus: updatedTable.current_order.order_cancellation_requests[0].approval_status,
                      requestedAt: updatedTable.current_order.order_cancellation_requests[0].requested_at
                    } : undefined,
                    pendingAdditionalItems: updatedTable.current_order.pending_additional_items || undefined,
                    pendingAdditionalItemsStatus: updatedTable.current_order.pending_additional_items_status || undefined
                  } : undefined,
                  reservation: updatedTable.reservation ? {
                    id: updatedTable.reservation.id,
                    customerName: updatedTable.reservation.customer_name,
                    customerPhone: updatedTable.reservation.customer_phone,
                    partySize: updatedTable.reservation.party_size,
                    reservationTime: updatedTable.reservation.reservation_time,
                    status: updatedTable.reservation.status
                  } : undefined
                }
                setSelectedTable(transformedTable)
                
                // If we found an order, reset the refresh attempt count
                if (transformedTable.currentOrder) {
                  setRefreshAttemptCount(0)
                }
                
                console.log('✅ [Frontend] Refreshed table after finding no currentOrder:', {
                  tableId: transformedTable.id,
                  hasCurrentOrder: !!transformedTable.currentOrder,
                  currentOrderId: transformedTable.currentOrder?.id,
                  currentOrderStatus: transformedTable.currentOrder?.status,
                  refreshAttemptCount: refreshAttemptCount
                })
              } else {
                console.warn('⚠️ [Frontend] Table fetch succeeded but data structure is invalid:', {
                  hasSuccess: !!data?.success,
                  hasData: !!data?.data,
                  tableId: selectedTable.id
                })
              }
            } catch (error) {
              console.error('❌ [Frontend] Error refreshing single table:', error)
              if (retryCount < maxRetries) {
                console.log(`🔄 [Frontend] Retrying after error (attempt ${retryCount + 2}/${maxRetries + 1})...`)
                await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second before retry
                return fetchTableWithRetry(retryCount + 1)
              }
            } finally {
              setIsRefreshingTable(false)
            }
          }
          
          // Start the fetch with retry
          fetchTableWithRetry()
        }
      }
    }
  }, [selectedTable, showTableSettingsModal, isRefreshingTable, lastTableId])

  // Also check bill request status when the order changes
  useEffect(() => {
    if (selectedTable?.currentOrder?.id) {
      checkBillRequestStatus(selectedTable.currentOrder.id)
    }
  }, [selectedTable?.currentOrder?.id])

  const loadData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        loadTables(),
        loadProducts(),
        loadAllergens(),
        loadRestaurantSettings(),
        loadPOSSettings(),
        loadFloorPlans(),
        loadOffers(),
        checkRegisterStatus()
      ])
    } catch (error) {
      console.error('Error loading data:', error)
      setToast({ message: 'Failed to load data', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const checkRegisterStatus = async () => {
    try {
      const response = await apiService.getCashRegisterStatus()
      // The API returns { success: true, isOpen: true, ... } directly
      // The request method returns the backend response as-is
      const isOpen = (response as any).isOpen ?? (response.data as any)?.isOpen ?? false
      if (response.success) {
        setRegisterStatus({ isOpen: isOpen })
      } else {
        setRegisterStatus({ isOpen: false })
      }
    } catch (error) {
      console.error('Error checking register status:', error)
      setRegisterStatus({ isOpen: false })
    }
  }

  const loadFloorPlans = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/floor-plans/all`)
      const data = await response.json()
      if (data.success) {
        setFloorPlans(data.data || [])
      }
    } catch (error) {
      console.error('Error loading floor plans:', error)
    }
  }

  const loadOffers = async () => {
    try {
      const response = await apiService.getOffers()
      if (response.success && response.data && Array.isArray(response.data)) {
        const transformedOffers = (response.data as any[]).map((offer: any) => ({
          id: offer.id,
          name: offer.name,
          description: offer.description,
          type: offer.type,
          value: Number(offer.value || 0),
          isActive: offer.is_active,
          applicableItems: offer.applicable_items || [],
          minOrderAmount: offer.min_order_amount,
          maxDiscount: offer.max_discount,
          comboItems: offer.combo_items || [],
          bogoItems: offer.bogo_items || []
        }))
        setAvailableOffers(transformedOffers)
      } else {
        console.error('Failed to load offers:', response.error)
        setAvailableOffers([])
      }
    } catch (error) {
      console.error('Error loading offers:', error)
      setAvailableOffers([])
    }
  }

  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        setRestaurantSettings(response.data)
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  const loadPOSSettings = async () => {
    try {
      const response = await apiService.getPOSSettings()
      if (response.success && response.data) {
        setPosSettings(response.data)
      }
    } catch (error) {
      console.error('Error loading POS settings:', error)
    }
  }

  const loadTables = async (switchToTableId?: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const url = selectedFloorPlanId && selectedFloorPlanId !== 'all' 
        ? `${apiUrl}/tables?floor_plan_id=${selectedFloorPlanId}`
        : `${apiUrl}/tables`
      const response = await fetch(url)
      const data = await response.json()
      if (data.success) {
        // Transform tables to include customer count and merged tables
        const transformedTables = (data.data || []).map((table: any) => {
          const mapOrder = (order: any) => {
            if (!order) return undefined
            const show = (order.status !== 'draft' && order.status !== 'waiting_for_approval') ||
              (order.status === 'completed' && (table.status === 'occupied' || table.status === 'waiting_for_approval'))
            if (!show) return undefined
            return {
              id: order.id,
              orderNumber: order.order_number,
              tableId: table.id,
              customerName: order.customer_name,
              customerPhone: order.customer_phone,
              customerId: order.customer_id,
              status: order.status,
              approvalStatus: order.approval_status,
              total: parseFloat(order.total ?? order.total_amount ?? 0),
              subtotal: parseFloat(order.subtotal ?? 0),
              taxAmount: parseFloat(order.tax_amount ?? 0),
              serviceCharge: parseFloat(order.service_charge ?? 0),
              discountAmount: parseFloat(order.discount_amount ?? 0),
              items: (order.order_items || []).map((item: any) => ({
                id: item.id,
                itemName: item.item_name,
                quantity: item.quantity,
                unitPrice: parseFloat(item.unit_price || 0),
                totalPrice: parseFloat(item.total_price || 0),
                status: item.status || 'pending',
                priority: item.priority,
                menuItemId: item.menu_item_id || undefined,
                itemCode: item.item_code || undefined
              })),
              itemsForModification: (order.order_items_for_modification || order.order_items || []).map((item: any) => ({
                id: item.id,
                itemName: item.item_name,
                quantity: item.quantity,
                unitPrice: parseFloat(item.unit_price || 0),
                totalPrice: parseFloat(item.total_price || 0),
                status: item.status || 'pending',
                priority: item.priority,
                menuItemId: item.menu_item_id || undefined,
                itemCode: item.item_code || undefined
              })),
              createdAt: order.created_at,
              modificationRequest: order.order_modification_requests?.[0] ? {
                id: order.order_modification_requests[0].id,
                modificationType: order.order_modification_requests[0].modification_type,
                requestedChanges: order.order_modification_requests[0].requested_changes,
                reason: order.order_modification_requests[0].reason,
                approvalStatus: order.order_modification_requests[0].approval_status,
                requestedAt: order.order_modification_requests[0].requested_at
              } : undefined,
              cancellationRequest: order.order_cancellation_requests?.[0] ? {
                id: order.order_cancellation_requests[0].id,
                cancellationReason: order.order_cancellation_requests[0].cancellation_reason,
                reasonDetails: order.order_cancellation_requests[0].reason_details,
                approvalStatus: order.order_cancellation_requests[0].approval_status,
                requestedAt: order.order_cancellation_requests[0].requested_at
              } : undefined,
              pendingAdditionalItems: order.pending_additional_items || undefined,
              pendingAdditionalItemsStatus: order.pending_additional_items_status || undefined
            }
          }
          return {
            id: table.id,
            number: table.number,
            capacity: table.capacity,
            status: table.status || 'available',
            location: (table.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
            floor_plan_id: table.floor_plan_id,
            customerCount: table.customer_count || 0,
            additionalChairs: table.additional_chairs || 0,
            mergedTables: table.merged_tables || [],
            updated_at: table.updated_at,
            currentOrder: mapOrder(table.current_order),
            orders: (table.orders || []).map((o: any) => mapOrder(o)).filter(Boolean),
            reservation: table.reservation ? {
              id: table.reservation.id,
              customerName: table.reservation.customer_name,
              customerPhone: table.reservation.customer_phone,
              partySize: table.reservation.party_size,
              reservationTime: table.reservation.reservation_time,
              status: table.reservation.status
            } : undefined
          }
        })
        setTables(transformedTables)
        
        // If we need to switch to a specific table after loading
        if (switchToTableId) {
          const targetTable = transformedTables.find((t: Table) => t.id === switchToTableId)
          if (targetTable) {
            setSelectedTable(targetTable)
          }
        }
      }
    } catch (error) {
      console.error('Error loading tables:', error)
    }
  }

  const refreshSingleTableSnapshot = async (tableId: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
    const response = await fetch(`${apiUrl}/tables/${tableId}`)
    const data = await response.json()
    if (!data?.success || !data?.data) return

    const updatedTable = data.data
    const transformedTable: Table = {
      id: updatedTable.id,
      number: updatedTable.number,
      capacity: updatedTable.capacity,
      status: updatedTable.status || 'available',
      location: (updatedTable.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
      floor_plan_id: updatedTable.floor_plan_id,
      customerCount: updatedTable.customer_count || 0,
      additionalChairs: updatedTable.additional_chairs || 0,
      mergedTables: updatedTable.merged_tables || [],
      currentOrder: updatedTable.current_order &&
        ((updatedTable.current_order.status !== 'draft' &&
          updatedTable.current_order.status !== 'waiting_for_approval') ||
         (updatedTable.current_order.status === 'completed' &&
          (updatedTable.status === 'occupied' || updatedTable.status === 'waiting_for_approval'))) ? {
        id: updatedTable.current_order.id,
        orderNumber: updatedTable.current_order.order_number,
        tableId: updatedTable.id,
        customerName: updatedTable.current_order.customer_name,
        customerPhone: updatedTable.current_order.customer_phone,
        customerId: updatedTable.current_order.customer_id,
        status: updatedTable.current_order.status,
        approvalStatus: updatedTable.current_order.approval_status,
        total: parseFloat(updatedTable.current_order.total || updatedTable.current_order.total_amount || 0),
        subtotal: parseFloat(updatedTable.current_order.subtotal || 0),
        taxAmount: parseFloat(updatedTable.current_order.tax_amount || 0),
        serviceCharge: parseFloat(updatedTable.current_order.service_charge || 0),
        discountAmount: parseFloat(updatedTable.current_order.discount_amount || 0),
        items: (updatedTable.current_order.order_items || []).map((item: any) => ({
          id: item.id,
          itemName: item.item_name,
          quantity: item.quantity,
          unitPrice: parseFloat(item.unit_price || 0),
          totalPrice: parseFloat(item.total_price || 0),
          status: item.status || 'pending',
          priority: item.priority,
          menuItemId: item.menu_item_id || undefined,
          itemCode: item.item_code || undefined
        })),
        itemsForModification: (updatedTable.current_order.order_items_for_modification || updatedTable.current_order.order_items || []).map((item: any) => ({
          id: item.id,
          itemName: item.item_name,
          quantity: item.quantity,
          unitPrice: parseFloat(item.unit_price || 0),
          totalPrice: parseFloat(item.total_price || 0),
          status: item.status || 'pending',
          priority: item.priority,
          menuItemId: item.menu_item_id || undefined,
          itemCode: item.item_code || undefined
        })),
        createdAt: updatedTable.current_order.created_at,
        modificationRequest: updatedTable.current_order.order_modification_requests?.[0] ? {
          id: updatedTable.current_order.order_modification_requests[0].id,
          modificationType: updatedTable.current_order.order_modification_requests[0].modification_type,
          requestedChanges: updatedTable.current_order.order_modification_requests[0].requested_changes,
          reason: updatedTable.current_order.order_modification_requests[0].reason,
          approvalStatus: updatedTable.current_order.order_modification_requests[0].approval_status,
          requestedAt: updatedTable.current_order.order_modification_requests[0].requested_at
        } : undefined,
        cancellationRequest: updatedTable.current_order.order_cancellation_requests?.[0] ? {
          id: updatedTable.current_order.order_cancellation_requests[0].id,
          cancellationReason: updatedTable.current_order.order_cancellation_requests[0].cancellation_reason,
          reasonDetails: updatedTable.current_order.order_cancellation_requests[0].reason_details,
          approvalStatus: updatedTable.current_order.order_cancellation_requests[0].approval_status,
          requestedAt: updatedTable.current_order.order_cancellation_requests[0].requested_at
        } : undefined,
        pendingAdditionalItems: updatedTable.current_order.pending_additional_items || undefined,
        pendingAdditionalItemsStatus: updatedTable.current_order.pending_additional_items_status || undefined
      } : undefined
    }

    setSelectedTable(prev => prev && prev.id === tableId ? transformedTable : prev)
    setTables(prev => prev.map(t => t.id === tableId ? transformedTable : t))
  }

  useEffect(() => {
    loadTables()
  }, [selectedFloorPlanId])

  // Default to waiter's assigned floor when they have one (once, on initial load)
  const hasSetWaiterDefaultFloor = useRef(false)
  useEffect(() => {
    if (hasSetWaiterDefaultFloor.current || !floorPlans.length || !(user as any)?.default_floor_plan_id) return
    const floorId = (user as any).default_floor_plan_id
    if (floorPlans.some(fp => fp.id === floorId)) {
      setSelectedFloorPlanId(floorId)
      hasSetWaiterDefaultFloor.current = true
    }
  }, [floorPlans, user])

  // Update timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Format elapsed time
  const formatElapsedTime = (startTime: string): string => {
    const start = new Date(startTime)
    const elapsed = Math.floor((currentTime.getTime() - start.getTime()) / 1000) // seconds
    
    if (elapsed < 60) {
      return `${elapsed}s`
    } else if (elapsed < 3600) {
      const minutes = Math.floor(elapsed / 60)
      const seconds = elapsed % 60
      return `${minutes}m ${seconds}s`
    } else {
      const hours = Math.floor(elapsed / 3600)
      const minutes = Math.floor((elapsed % 3600) / 60)
      return `${hours}h ${minutes}m`
    }
  }

  const updateTableStatusToOccupied = async (tableId: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/tables/${tableId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'occupied' })
      })
      const data = await response.json()
      if (data.success) {
        // Instead of calling loadTables() which can cause 500 errors,
        // just fetch the single table and update selectedTable if needed
        if (selectedTable?.id === tableId) {
          try {
            const singleTableResponse = await fetch(`${apiUrl}/tables/${tableId}`)
            if (!singleTableResponse.ok) {
              console.error('Failed to fetch updated table:', singleTableResponse.status)
              return
            }
            const singleTableData = await singleTableResponse.json()
            if (singleTableData.success && singleTableData.data) {
              const updatedTable = singleTableData.data
              // Transform the table data the same way as in loadTables
              const transformedTable: Table = {
                id: updatedTable.id,
                number: updatedTable.number,
                capacity: updatedTable.capacity,
                status: updatedTable.status || 'available',
                location: (updatedTable.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
                floor_plan_id: updatedTable.floor_plan_id,
                customerCount: updatedTable.customer_count || 0,
                additionalChairs: updatedTable.additional_chairs || 0,
                mergedTables: updatedTable.merged_tables || [],
                    // Show order only if:
                    // 1. Approved (not draft/waiting_for_approval) - show order details
                    // 2. OR completed but table is occupied (for invoice requests)
                    currentOrder: updatedTable.current_order && 
                      ((updatedTable.current_order.status !== 'draft' && 
                        updatedTable.current_order.status !== 'waiting_for_approval') ||
                       (updatedTable.current_order.status === 'completed' && 
                        (updatedTable.status === 'occupied' || updatedTable.status === 'waiting_for_approval'))) ? {
                  id: updatedTable.current_order.id,
                  orderNumber: updatedTable.current_order.order_number,
                  tableId: updatedTable.id,
                  customerName: updatedTable.current_order.customer_name,
                  customerPhone: updatedTable.current_order.customer_phone,
                  customerId: updatedTable.current_order.customer_id,
                  status: updatedTable.current_order.status,
                  approvalStatus: updatedTable.current_order.approval_status,
                  total: parseFloat(updatedTable.current_order.total || updatedTable.current_order.total_amount || 0),
                  subtotal: parseFloat(updatedTable.current_order.subtotal || 0),
                  taxAmount: parseFloat(updatedTable.current_order.tax_amount || 0),
                  serviceCharge: parseFloat(updatedTable.current_order.service_charge || 0),
                  discountAmount: parseFloat(updatedTable.current_order.discount_amount || 0),
                  items: (updatedTable.current_order.order_items || []).map((item: any) => ({
                    id: item.id,
                    itemName: item.item_name,
                    quantity: item.quantity,
                    unitPrice: parseFloat(item.unit_price || 0),
                    totalPrice: parseFloat(item.total_price || 0),
                    status: item.status || 'pending',
                    priority: item.priority,
                    menuItemId: item.menu_item_id || undefined,
                    itemCode: item.item_code || undefined
                  })),
                  itemsForModification: (updatedTable.current_order.order_items_for_modification || updatedTable.current_order.order_items || []).map((item: any) => ({
                    id: item.id,
                    itemName: item.item_name,
                    quantity: item.quantity,
                    unitPrice: parseFloat(item.unit_price || 0),
                    totalPrice: parseFloat(item.total_price || 0),
                    status: item.status || 'pending',
                    priority: item.priority,
                    menuItemId: item.menu_item_id || undefined,
                    itemCode: item.item_code || undefined
                  })),
                  createdAt: updatedTable.current_order.created_at,
                  modificationRequest: updatedTable.current_order.order_modification_requests?.[0] ? {
                    id: updatedTable.current_order.order_modification_requests[0].id,
                    modificationType: updatedTable.current_order.order_modification_requests[0].modification_type,
                    requestedChanges: updatedTable.current_order.order_modification_requests[0].requested_changes,
                    reason: updatedTable.current_order.order_modification_requests[0].reason,
                    approvalStatus: updatedTable.current_order.order_modification_requests[0].approval_status,
                    requestedAt: updatedTable.current_order.order_modification_requests[0].requested_at
                  } : undefined,
                  cancellationRequest: updatedTable.current_order.order_cancellation_requests?.[0] ? {
                    id: updatedTable.current_order.order_cancellation_requests[0].id,
                    cancellationReason: updatedTable.current_order.order_cancellation_requests[0].cancellation_reason,
                    reasonDetails: updatedTable.current_order.order_cancellation_requests[0].reason_details,
                    approvalStatus: updatedTable.current_order.order_cancellation_requests[0].approval_status,
                    requestedAt: updatedTable.current_order.order_cancellation_requests[0].requested_at
                  } : undefined,
                  pendingAdditionalItems: updatedTable.current_order.pending_additional_items || undefined,
                  pendingAdditionalItemsStatus: updatedTable.current_order.pending_additional_items_status || undefined
                } : undefined,
                reservation: updatedTable.reservation ? {
                  id: updatedTable.reservation.id,
                  customerName: updatedTable.reservation.customer_name,
                  customerPhone: updatedTable.reservation.customer_phone,
                  partySize: updatedTable.reservation.party_size,
                  reservationTime: updatedTable.reservation.reservation_time,
                  status: updatedTable.reservation.status
                } : undefined
              }
              setSelectedTable(transformedTable)
              // Debug logging commented out - uncomment for debugging if needed
              // console.log('✅ Updated selected table after status change:', {
              //   tableId: transformedTable.id,
              //   tableNumber: transformedTable.number,
              //   status: transformedTable.status,
              //   hasCurrentOrder: !!transformedTable.currentOrder
              // })
            }
          } catch (error) {
            console.error('Error fetching updated table:', error)
            // Don't throw - just log the error
          }
        }
      }
    } catch (error) {
      console.error('Error updating table status:', error)
    }
  }

  const loadAllergens = async () => {
    try {
      const res = await apiService.getAllergens()
      if (res.success && res.data && Array.isArray(res.data)) {
        const map: Record<string, AllergenInfo> = {}
        ;(res.data as Array<{ id: string; name: string; icon: string | null }>).forEach((a) => {
          map[a.id] = { id: a.id, name: a.name, icon: a.icon }
        })
        setAllergenMap(map)
      }
    } catch (_) {}
  }

  const loadProducts = async () => {
    try {
      const response = await apiService.getMenuItems()
      if (response.success && response.data && Array.isArray(response.data)) {
        const transformedProducts = response.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          basePrice: Number(item.base_price || 0),
          categoryId: item.category_id,
          code: item.code ?? '',
          image: resolveImageUrl(item.image),
          description: item.description,
          isActive: item.is_active,
          calories: item.calories,
          stepsToBurn: item.steps_to_burn,
          allergens: item.allergens || [],
          variants: item.variants || [],
          addons: item.addons || []
        }))
        setProducts(transformedProducts)
      }
    } catch (error) {
      console.error('Error loading products:', error)
    }
  }

  const loadCustomerById = async (customerId: string) => {
    try {
      const response = await apiService.getCustomers()
      if (response.success && response.data && Array.isArray(response.data)) {
        const customer = (response.data as any[]).find((c: any) => c.id === customerId)
        if (customer) {
          setSelectedCustomer({
            id: customer.id,
            name: customer.name,
            mobile_number: customer.mobile_number,
            phone: customer.phone,
            first_name: customer.first_name,
            last_name: customer.last_name
          })
        }
      }
    } catch (error) {
      console.error('Error loading customer:', error)
    }
  }

  const handleCustomerSearch = async (mobileNumber: string) => {
    if (!mobileNumber.trim()) {
      setSelectedCustomer(null)
      setCustomerVisitCount(null)
      return
    }

    setIsSearchingCustomer(true)
    try {
      const response = await apiService.getCustomerByMobile(mobileNumber)
      if (response.success && response.data) {
        const customerData = response.data as any
        setSelectedCustomer({
          id: customerData.id,
          name: customerData.name,
          mobile_number: customerData.mobile_number,
          phone: customerData.phone,
          first_name: customerData.first_name,
          last_name: customerData.last_name
        })
        
        // Fetch visit count
        try {
          const visitCountResponse = await apiService.getCustomerVisitCount(customerData.id)
          if (visitCountResponse.success) {
            const count = (visitCountResponse as any).visitCount !== undefined 
              ? (visitCountResponse as any).visitCount 
              : ((visitCountResponse.data as any)?.visitCount || 0)
            setCustomerVisitCount(count)
          } else {
            setCustomerVisitCount(0)
          }
        } catch (error) {
          console.error('Error fetching visit count:', error)
          setCustomerVisitCount(0)
        }
        
        setToast({ message: 'Customer found', type: 'success' })
      } else {
        setSelectedCustomer(null)
        setCustomerVisitCount(null)
        setToast({ message: 'Customer not found. Will create as guest.', type: 'error' })
      }
    } catch (error) {
      console.error('Error searching customer:', error)
      setSelectedCustomer(null)
      setCustomerVisitCount(null)
    } finally {
      setIsSearchingCustomer(false)
    }
  }
  
  const handleClearCustomerMobile = () => {
    setCustomerMobile('')
    setSelectedCustomer(null)
    setCustomerVisitCount(null)
    setShowMobileKeypad(false)
  }

  const getCategoryPriority = (categoryId: string): number => {
    const category = getAllCategoriesFlat().find(c => c.id === categoryId)
    return (category as any)?.defaultPriority || 1
  }

  const calculateNextPriority = (categoryId: string, _existingOrderItems?: OrderItem[]): number => {
    // Use the category's default priority directly
    // Multiple items from the same category will share the same priority
    const categoryPriority = getCategoryPriority(categoryId)
    
    // Check if there are any items with lower category priorities that need to be filled
    // const allItems = existingOrderItems ? 
    //   [...cart.map(item => ({ categoryId: item.categoryId, categoryPriority: item.categoryPriority, priority: item.priority })), 
    //    ...existingOrderItems.map(item => ({ categoryId: '', categoryPriority: 0, priority: item.priority || 0 }))] :
    //   cart.map(item => ({ categoryId: item.categoryId, categoryPriority: item.categoryPriority, priority: item.priority }))
    
    // Find the highest priority that exists in cart/order
    // const existingPriorities = allItems.map(item => item.priority).filter(p => p > 0)
    // const maxExistingPriority = existingPriorities.length > 0 ? Math.max(...existingPriorities) : 0
    
    // If the category priority is higher than any existing priority, use it directly
    // If there are gaps (e.g., we have priority 1 items and add priority 3 category),
    // we still use the category priority (3), not fill the gap (2)
    return categoryPriority
  }

  const handleAddToCart = (product: Product) => {
    setSelectedProduct(product)
    setSelectedVariant(null)
    setSelectedAddons([])
    setItemNotes('')
    setShowCustomizationModal(true)
  }

  const handleConfirmAddToCart = () => {
    if (!selectedProduct) return

    const variant = selectedVariant || selectedProduct.variants?.find(v => v.isDefault) || null
    const basePrice = selectedProduct.basePrice
    const variantModifier = variant?.priceModifier || 0
    const addonsPrice = selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
    
    // Calculate base price
    let mainItemPrice = basePrice + variantModifier
    const originalPrice = mainItemPrice + addonsPrice
    
    // Check if any applied discount offers should apply to this new item
    let finalPrice = originalPrice
    let discountOfferId: string | undefined
    let discountOriginalPrice: number | undefined
    let discountType: 'percentage' | 'fixed' | undefined
    let discountValue: number | undefined
    
    // Check applied discount offers
    for (const offer of appliedOffers) {
      if ((offer.type === 'percentage' || offer.type === 'fixed') && 
          offer.applicableItems && 
          offer.applicableItems.includes(selectedProduct.id)) {
        // Apply discount to main item only (not addons)
        let discountedMainItemPrice = mainItemPrice
        
        if (offer.type === 'percentage') {
          const discountAmount = (mainItemPrice * offer.value) / 100
          const maxDiscount = offer.maxDiscount ? (mainItemPrice * offer.maxDiscount) / 100 : discountAmount
          discountedMainItemPrice = mainItemPrice - Math.min(discountAmount, maxDiscount)
        } else if (offer.type === 'fixed') {
          discountedMainItemPrice = Math.max(0, mainItemPrice - offer.value)
        }
        
        // Final price = discounted main item + full addon price
        finalPrice = discountedMainItemPrice + addonsPrice
        discountOfferId = offer.id
        discountOriginalPrice = originalPrice
        discountType = offer.type as 'percentage' | 'fixed'
        discountValue = offer.value
        break // Apply only the first matching offer
      }
    }

    const categoryPriority = getCategoryPriority(selectedProduct.categoryId)
    const existingOrderItems = selectedTable?.currentOrder?.items || []
    const priority = calculateNextPriority(selectedProduct.categoryId, existingOrderItems)

    const notesNormalized = itemNotes.trim() || undefined

    const cartItem: CartItem = {
      id: `${Date.now()}-${Math.random()}`,
      name: selectedProduct.name,
      basePrice,
      finalPrice,
      product: selectedProduct,
      quantity: 1,
      notes: notesNormalized,
      selectedVariant: variant || undefined,
      selectedAddons: selectedAddons.length > 0 ? selectedAddons : undefined,
      priority,
      categoryId: selectedProduct.categoryId,
      categoryPriority,
      discountOfferId,
      discountOriginalPrice,
      discountType,
      discountValue
    }

    setCart((prev) => {
      const mergeIdx = prev.findIndex((c) => waiterCartItemsMergeable(c, cartItem))
      if (mergeIdx < 0) return [...prev, cartItem]
      return prev.map((c, i) => {
        if (i !== mergeIdx) return c
        const newQuantity = c.quantity + 1
        if (c.isBOGOItem) {
          const paidQuantity = Math.ceil(newQuantity / 2)
          const freeQuantity = Math.floor(newQuantity / 2)
          return { ...c, quantity: newQuantity, paidQuantity, freeQuantity }
        }
        return { ...c, quantity: newQuantity }
      })
    })
    setShowCustomizationModal(false)
    setSelectedProduct(null)
    setSelectedVariant(null)
    setSelectedAddons([])
    setItemNotes('')
    
    // Update table status to "occupied" if a table is selected
    if (selectedTable && selectedTable.status !== 'occupied') {
      updateTableStatusToOccupied(selectedTable.id)
    }
  }

  const handleRemoveFromCart = (itemId: string) => {
    setCart(cart.filter(item => item.id !== itemId))
  }

  const handleUpdateQuantity = (itemId: string, delta: number) => {
    setCart(cart.map(item => {
      if (item.id === itemId) {
        const newQuantity = Math.max(1, item.quantity + delta)
        
        // If it's a BOGO item, recalculate paid/free quantities
        if (item.isBOGOItem) {
          // For "buy 1 get 1 free": paid = ceil(quantity/2), free = floor(quantity/2)
          const paidQuantity = Math.ceil(newQuantity / 2)
          const freeQuantity = Math.floor(newQuantity / 2)
          return { 
            ...item, 
            quantity: newQuantity,
            paidQuantity,
            freeQuantity
          }
        }
        
        // finalPrice is the unit price - it should NOT change when quantity changes
        // The total is calculated as finalPrice * quantity when displaying
        return { ...item, quantity: newQuantity }
      }
      return item
    }))
  }

  const loadCartFromQRPayload = (payload: OrderQRPayload) => {
    const cartItems: CartItem[] = []
    const existingOrderItems = selectedTable?.currentOrder?.items || []

    for (const row of payload.i) {
      const product = products.find(p => p.id === row.mi)
      if (!product) {
        console.warn('Product not found for QR item:', row.mi, row.na)
        continue
      }
      const variant = row.vn != null
        ? product.variants?.find(v => v.name === row.vn)
        : (product.variants?.find(v => v.isDefault) || undefined)
      const variantModifier = variant?.priceModifier ?? row.vpm ?? 0
      const addonNames = row.ad ?? []
      const selectedAddons: Addon[] = []
      if (product.addons?.length && addonNames.length) {
        for (const name of addonNames) {
          const addon = product.addons.find(a => a.name === name)
          if (addon) selectedAddons.push(addon)
        }
      }
      const addonsPrice = selectedAddons.reduce((s, a) => s + a.price, 0)
      const basePrice = product.basePrice + variantModifier
      const finalPrice = basePrice + addonsPrice
      const categoryPriority = getCategoryPriority(product.categoryId)
      const priority = calculateNextPriority(product.categoryId, existingOrderItems)

      cartItems.push({
        id: `${Date.now()}-${Math.random()}-${row.mi}`,
        name: product.name,
        basePrice,
        finalPrice,
        product,
        quantity: row.q,
        notes: row.no ?? undefined,
        selectedVariant: variant,
        selectedAddons: selectedAddons.length > 0 ? selectedAddons : undefined,
        priority,
        categoryId: product.categoryId,
        categoryPriority
      })
    }

    setCart(cartItems)
    setOrderNotes(payload.n ?? '')
    if (payload.cm) setCustomerMobile(String(payload.cm).trim())
    if (selectedTable && cartItems.length > 0 && selectedTable.status !== 'occupied') {
      updateTableStatusToOccupied(selectedTable.id)
    }
  }

  const handleScanQRFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScanQRError(null)
    const file = e.target.files?.[0]
    if (!file) return
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        setScanQRError('Could not read image')
        return
      }
      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const result = jsQR(imageData.data, imageData.width, imageData.height)
      if (!result?.data) {
        setScanQRError('No QR code found in image')
        return
      }
      try {
        const payload = JSON.parse(result.data) as OrderQRPayload
        if (payload.v !== 1 || !Array.isArray(payload.i) || payload.i.length === 0) {
          setScanQRError('Invalid order QR format')
          return
        }
        loadCartFromQRPayload(payload)
        setShowScanQRModal(false)
        setToast({ message: 'Order loaded from QR', type: 'success' })
      } catch {
        setScanQRError('Invalid QR code content')
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      setScanQRError('Failed to load image')
    }
    img.src = url
    e.target.value = ''
  }

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault()
    if (!draggedItem || draggedItem === targetItemId) return

    const items = Array.from(cart)
    const draggedIndex = items.findIndex(item => item.id === draggedItem)
    const targetIndex = items.findIndex(item => item.id === targetItemId)

    if (draggedIndex === -1 || targetIndex === -1) return

    const [reorderedItem] = items.splice(draggedIndex, 1)
    items.splice(targetIndex, 0, reorderedItem)

    const updatedItems = items.map((item, index) => ({
      ...item,
      priority: index + 1
    }))

    setCart(updatedItems)
    setDraggedItem(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
  }

  // Save current cart for the selected table
  const saveCartForTable = (tableId: string) => {
    if (tableId && cart.length > 0) {
      setTableCarts(prev => ({
        ...prev,
        [tableId]: [...cart]
      }))
    }
  }

  // Restore cart for a table (if any saved items exist)
  const restoreCartForTable = (tableId: string) => {
    const savedCart = tableCarts[tableId]
    if (savedCart && savedCart.length > 0) {
      setCart(savedCart)
    }
  }

  // Clear saved cart for a table
  const clearSavedCartForTable = (tableId: string) => {
    setTableCarts(prev => {
      const newCarts = { ...prev }
      delete newCarts[tableId]
      return newCarts
    })
  }

  // Get gross total (VAT-inclusive sum of all items)
  const getGrossTotal = () => {
    const cartTotal = cart.reduce((sum, item) => {
      // For combo items, use combo price
      if (item.isComboItem && item.comboPrice !== undefined) {
        return sum + (item.comboPrice * item.quantity)
      }
      // For BOGO items, calculate based on paid/free quantities
      if (item.isBOGOItem && item.paidQuantity !== undefined && item.freeQuantity !== undefined) {
        const unitPrice = item.originalPrice || item.basePrice || item.finalPrice
        // For free BOGO: only pay for paid quantity
        if (item.bogoDiscountType === 'free') {
          return sum + (unitPrice * item.paidQuantity)
        } else {
          // For percentage/fixed BOGO discounts, calculate accordingly
          return sum + (unitPrice * item.paidQuantity) + ((item.finalPrice || 0) * item.freeQuantity)
        }
      }
      // For free items (visit-based offers)
      if (item.isFreeItem) {
        return sum + 0
      }
      // For items with discount offers, use the finalPrice (which already includes discounted main item + addons)
      if (item.discountOfferId && item.finalPrice !== undefined) {
        return sum + (item.finalPrice * item.quantity)
      }
      return sum + (item.finalPrice * item.quantity)
    }, 0)

    return isNaN(cartTotal) ? 0 : cartTotal
  }

  // Get subtotal (NET amount = Gross minus VAT)
  const getSubtotal = () => {
    const grossTotal = getGrossTotal()
    const discountAmount = getDiscountAmount()
    const grossAfterDiscount = grossTotal - discountAmount
    const taxRate = restaurantSettings?.tax_rate || 15
    // Net = Gross / (1 + VAT rate)
    const netAmount = grossAfterDiscount / (1 + taxRate / 100)
    return isNaN(netAmount) ? 0 : Math.round(netAmount * 100) / 100
  }

  const getDiscountAmount = () => {
    let discountAmount = 0

    // Applied offers - only count combo/BOGO discounts in summary
    // Percentage/fixed discount offers are already applied to item prices, so don't count them here
    appliedOffers.forEach(offer => {
      if (offer.type === 'combo' || offer.type === 'bogo') {
        // Combo and BOGO offers - calculate discount from original prices
        cart.forEach(item => {
          if (item.isComboItem && item.originalPrice) {
            discountAmount += (item.originalPrice - (item.comboPrice || item.finalPrice)) * item.quantity
          } else if (item.isBOGOItem && item.originalPrice) {
            const paidPrice = item.originalPrice
            const paidTotal = paidPrice * (item.paidQuantity || 0)
            const freeTotal = (item.finalPrice || 0) * (item.freeQuantity || 0)
            const originalTotal = paidPrice * item.quantity
            discountAmount += originalTotal - (paidTotal + freeTotal)
          }
        })
      }
      // Don't count percentage/fixed discount offers here as they're already in item prices
    })

    return isNaN(discountAmount) ? 0 : discountAmount
  }

  const getTax = () => {
    // VAT-INCLUSIVE pricing: VAT is extracted from gross total
    const grossTotal = getGrossTotal()
    const discountAmount = getDiscountAmount()
    const grossAfterDiscount = grossTotal - discountAmount
    const taxRate = restaurantSettings?.tax_rate || 15 // KSA VAT is 15%
    
    // VAT = Gross - Net = Gross - (Gross / 1.15)
    const tax = grossAfterDiscount - (grossAfterDiscount / (1 + taxRate / 100))
    return isNaN(tax) ? 0 : Math.round(tax * 100) / 100
  }

  const getServiceCharge = () => {
    const netSubtotal = getSubtotal() // This is now the NET amount
    const serviceChargeRate = restaurantSettings?.service_charge || 0 // Percentage from settings
    // Service charge is calculated on the net amount
    const serviceCharge = netSubtotal * (serviceChargeRate / 100)
    return isNaN(serviceCharge) ? 0 : Math.round(serviceCharge * 100) / 100
  }

  const getTotal = () => {
    // Total = Subtotal (net) + VAT + Service Charge
    const netSubtotal = getSubtotal() // Net amount
    const tax = getTax()
    const serviceCharge = getServiceCharge()
    const total = netSubtotal + tax + serviceCharge
    return isNaN(total) ? 0 : Math.round(total * 100) / 100
  }

  const getApplicableOffers = () => {
    if (cart.length === 0) return []
    
    const cartProductIds = cart.map(item => item.product.id)
    const grossTotal = getGrossTotal() // Use gross for minimum order checks
    
    return availableOffers.filter(offer => {
      if (!offer.isActive) return false
      
      // Check minimum order amount (use gross total - what customer sees)
      if (offer.minOrderAmount && grossTotal < offer.minOrderAmount) return false
      
      // Check if already applied
      if (appliedOffers.find(o => o.id === offer.id)) return false
      
      // Check if cart has applicable items
      if (offer.type === 'combo') {
        const comboItemIds = offer.comboItems?.map((item: any) => item.menuItemId || item.id || item.productId) || []
        return comboItemIds.some(itemId => cartProductIds.includes(itemId))
      } else if (offer.type === 'bogo') {
        const bogoItemIds = offer.bogoItems?.map((item: any) => item.buyItemId || item.getItemId || item.menuItemId || item.id || item.productId) || []
        return bogoItemIds.some(itemId => cartProductIds.includes(itemId))
      } else {
        return offer.applicableItems.some(itemId => cartProductIds.includes(itemId))
      }
    })
  }

  const applyOffer = (offer: Offer) => {
    if (!appliedOffers.find(o => o.id === offer.id)) {
      if (offer.type === 'combo') {
        applyComboOffer(offer)
      } else if (offer.type === 'bogo') {
        applyBOGOOffer(offer)
      } else {
        // Handle regular offers (percentage, fixed) - apply discount to applicable items
        applyDiscountOffer(offer)
      }
    }
  }

  const applyDiscountOffer = (offer: Offer) => {
    if (offer.type !== 'percentage' && offer.type !== 'fixed') {
      return // Only handle percentage and fixed discount offers
    }
    
    const newCartItems: CartItem[] = cart.map(item => {
      // Check if this item is applicable for the offer
      if (offer.applicableItems && offer.applicableItems.includes(item.product.id)) {
        // Get the base price (main item + variant only, excluding addons)
        let mainItemPrice = item.product.basePrice
        if (item.selectedVariant) {
          mainItemPrice += item.selectedVariant.priceModifier
        }
        
        // Calculate addon prices separately (not discounted)
        const addonPrice = item.selectedAddons 
          ? item.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
          : 0
        
        // Original price is main item + addons (for display purposes)
        const originalPrice = mainItemPrice + addonPrice
        
        // Apply discount only to the main item price (not addons)
        let discountedMainItemPrice = mainItemPrice
        
        if (offer.type === 'percentage') {
          // Calculate percentage discount on main item only
          const discountAmount = (mainItemPrice * offer.value) / 100
          const maxDiscount = offer.maxDiscount ? (mainItemPrice * offer.maxDiscount) / 100 : discountAmount
          discountedMainItemPrice = mainItemPrice - Math.min(discountAmount, maxDiscount)
        } else if (offer.type === 'fixed') {
          // Apply fixed discount to main item only
          discountedMainItemPrice = Math.max(0, mainItemPrice - offer.value)
        }
        
        // Final price = discounted main item + full addon price
        const finalPrice = discountedMainItemPrice + addonPrice
        
        return {
          ...item,
          discountOfferId: offer.id,
          discountOriginalPrice: originalPrice,
          discountType: offer.type as 'percentage' | 'fixed',
          discountValue: offer.value,
          finalPrice: finalPrice
        } as CartItem
      }
      return item
    })
    
    setCart(newCartItems)
    setAppliedOffers([...appliedOffers, offer])
  }

  const applyComboOffer = (offer: Offer) => {
    if (!offer.comboItems || offer.comboItems.length === 0) return

    // const cartProductIds = cart.map(item => item.product.id)
    const newCartItems: CartItem[] = [...cart]

    offer.comboItems.forEach((comboItem: any) => {
      const itemId = comboItem.menuItemId || comboItem.id || comboItem.productId
      const existingItemIndex = newCartItems.findIndex(item => item.product.id === itemId)
      
      if (existingItemIndex >= 0) {
        newCartItems[existingItemIndex] = {
          ...newCartItems[existingItemIndex],
          isComboItem: true,
          comboPrice: comboItem.price || comboItem.comboPrice || 0,
          originalPrice: newCartItems[existingItemIndex].product.basePrice
        }
      } else {
        const product = products.find(p => p.id === itemId)
        if (product) {
          const categoryPriority = getCategoryPriority(product.categoryId)
          const existingOrderItems = selectedTable?.currentOrder?.items || []
          const priority = calculateNextPriority(product.categoryId, existingOrderItems)
          
          newCartItems.push({
            id: `${Date.now()}-${Math.random()}`,
            name: product.name,
            basePrice: product.basePrice,
            finalPrice: comboItem.price || comboItem.comboPrice || product.basePrice,
            product,
            quantity: 1,
            notes: '',
            selectedVariant: undefined,
            selectedAddons: [],
            priority,
            categoryId: product.categoryId,
            categoryPriority,
            isComboItem: true,
            comboPrice: comboItem.price || comboItem.comboPrice || 0,
            originalPrice: product.basePrice
          })
        }
      }
    })

    setCart(newCartItems)
    setAppliedOffers([...appliedOffers, offer])
  }

  const applyBOGOOffer = (offer: Offer) => {
    if (!offer.bogoItems || offer.bogoItems.length === 0) return

    const newCartItems: CartItem[] = [...cart]

    offer.bogoItems.forEach((bogoItem: any) => {
      const buyItemId = bogoItem.buyItemId || bogoItem.menuItemId || bogoItem.id || bogoItem.productId
      const buyItemIndex = newCartItems.findIndex(item => item.product.id === buyItemId)
      
      if (buyItemIndex >= 0) {
        const buyItem = newCartItems[buyItemIndex]
        let finalQuantity = buyItem.quantity
        if (finalQuantity < 2) {
          finalQuantity = 2
        }
        
        // For "buy 1 get 1 free": paid = ceil(quantity/2), free = floor(quantity/2)
        const paidQuantity = Math.ceil(finalQuantity / 2)
        const freeQuantity = Math.floor(finalQuantity / 2)
        
        // Calculate unit price including variant and addons
        let unitPrice = buyItem.product.basePrice
        if (buyItem.selectedVariant) {
          unitPrice += buyItem.selectedVariant.priceModifier
        }
        if (buyItem.selectedAddons) {
          unitPrice += buyItem.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
        }
        
        newCartItems[buyItemIndex] = {
          ...buyItem,
          quantity: finalQuantity,
          isBOGOItem: true,
          bogoDiscountType: bogoItem.discountType || 'free',
          bogoDiscountValue: bogoItem.discountValue || 0,
          originalPrice: unitPrice, // Store unit price including variants and addons
          paidQuantity: paidQuantity,
          freeQuantity: freeQuantity,
          finalPrice: unitPrice // Keep finalPrice as unit price for calculations
        }
      }
    })

    setCart(newCartItems)
    setAppliedOffers([...appliedOffers, offer])
  }

  const removeOffer = (offerId: string) => {
    const offer = appliedOffers.find(o => o.id === offerId)
    if (offer) {
      if (offer.type === 'combo') {
        const newCartItems = cart.map(item => {
          if (item.isComboItem) {
            return {
              ...item,
              isComboItem: false,
              comboPrice: undefined,
              originalPrice: undefined
            }
          }
          return item
        })
        setCart(newCartItems)
      } else if (offer.type === 'bogo') {
        const newCartItems = cart.map(item => {
          if (item.isBOGOItem) {
            return {
              ...item,
              isBOGOItem: false,
              bogoDiscountType: undefined,
              bogoDiscountValue: undefined,
              originalPrice: undefined,
              paidQuantity: undefined,
              freeQuantity: undefined
            }
          }
          return item
        })
        setCart(newCartItems)
      } else if (offer.type === 'percentage' || offer.type === 'fixed') {
        // Remove discount pricing and restore original pricing
        const newCartItems = cart.map(item => {
          if (item.discountOfferId === offerId) {
            // Restore original price
            let basePrice = item.product.basePrice
            if (item.selectedVariant) {
              basePrice += item.selectedVariant.priceModifier
            }
            if (item.selectedAddons) {
              basePrice += item.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
            }
            return {
              ...item,
              discountOfferId: undefined,
              discountOriginalPrice: undefined,
              discountType: undefined,
              discountValue: undefined,
              finalPrice: basePrice
            }
          }
          return item
        })
        setCart(newCartItems)
      }
      setAppliedOffers(appliedOffers.filter(o => o.id !== offerId))
    }
  }

  const handleUpdateTableSettings = async () => {
    if (!selectedTable) return

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const updateData: any = {
        customer_count: customerCount,
        additional_chairs: additionalChairs
      }

      // If customer count or additional chairs are set, update status to "occupied"
      if (customerCount > 0 || additionalChairs > 0) {
        updateData.status = 'occupied'
      }

      const response = await fetch(`${apiUrl}/tables/${selectedTable.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Table settings updated', type: 'success' })
        await loadTables()
        setShowTableSettingsModal(false)
        
        // If this was triggered from place order, proceed with placing the order
        if (pendingPlaceOrder) {
          setPendingPlaceOrder(false)
          // Continue with order placement after a short delay to ensure state is updated
          setTimeout(() => {
            processOrderPlacement()
          }, 100)
          return
        }
        
        // Update selectedTable with the new status if it was changed
        if (updateData.status === 'occupied' && selectedTable.status !== 'occupied') {
          const updatedTables = await fetch(`${apiUrl}/tables?floor_plan_id=${selectedFloorPlanId || 'all'}`)
            .then(res => res.json())
          if (updatedTables.success && updatedTables.data) {
            const updatedTable = updatedTables.data.find((t: Table) => t.id === selectedTable.id)
            if (updatedTable) {
              // Transform the table data the same way as in loadTables
              const transformedTable: Table = {
                id: updatedTable.id,
                number: updatedTable.number,
                capacity: updatedTable.capacity,
                status: updatedTable.status || 'available',
                location: (updatedTable.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
                floor_plan_id: updatedTable.floor_plan_id,
                customerCount: updatedTable.customer_count || 0,
                additionalChairs: updatedTable.additional_chairs || 0,
                mergedTables: updatedTable.merged_tables || [],
                currentOrder: updatedTable.current_order ? {
                  id: updatedTable.current_order.id,
                  orderNumber: updatedTable.current_order.order_number,
                  tableId: updatedTable.id,
                  customerName: updatedTable.current_order.customer_name,
                  customerPhone: updatedTable.current_order.customer_phone,
                  customerId: updatedTable.current_order.customer_id,
                  status: updatedTable.current_order.status,
                  total: parseFloat(updatedTable.current_order.total || updatedTable.current_order.total_amount || 0),
                  subtotal: parseFloat(updatedTable.current_order.subtotal || 0),
                  taxAmount: parseFloat(updatedTable.current_order.tax_amount || 0),
                  serviceCharge: parseFloat(updatedTable.current_order.service_charge || 0),
                  discountAmount: parseFloat(updatedTable.current_order.discount_amount || 0),
                  items: (updatedTable.current_order.order_items || []).map((item: any) => ({
                    id: item.id,
                    itemName: item.item_name,
                    quantity: item.quantity,
                    unitPrice: parseFloat(item.unit_price || 0),
                    totalPrice: parseFloat(item.total_price || 0),
                    status: item.status || 'pending',
                    priority: item.priority,
                    menuItemId: item.menu_item_id || undefined,
                    itemCode: item.item_code || undefined
                  })),
                  itemsForModification: (updatedTable.current_order.order_items_for_modification || updatedTable.current_order.order_items || []).map((item: any) => ({
                    id: item.id,
                    itemName: item.item_name,
                    quantity: item.quantity,
                    unitPrice: parseFloat(item.unit_price || 0),
                    totalPrice: parseFloat(item.total_price || 0),
                    status: item.status || 'pending',
                    priority: item.priority,
                    menuItemId: item.menu_item_id || undefined,
                    itemCode: item.item_code || undefined
                  })),
                  createdAt: updatedTable.current_order.created_at
                } : undefined,
                reservation: updatedTable.reservation ? {
                  id: updatedTable.reservation.id,
                  customerName: updatedTable.reservation.customer_name,
                  customerPhone: updatedTable.reservation.customer_phone,
                  partySize: updatedTable.reservation.party_size,
                  reservationTime: updatedTable.reservation.reservation_time,
                  status: updatedTable.reservation.status
                } : undefined
              }
              setSelectedTable(transformedTable)
            }
          }
        }
      } else {
        setToast({ message: data.error || 'Failed to update table', type: 'error' })
      }
    } catch (error) {
      console.error('Error updating table:', error)
      setToast({ message: 'Failed to update table', type: 'error' })
    }
  }

  const handleMergeTables = async () => {
    if (!selectedTable || tablesToMerge.length === 0) return

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const mergedTablesList = [...(selectedTable.mergedTables || []), ...tablesToMerge]
      
      const response = await fetch(`${apiUrl}/tables/${selectedTable.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merged_tables: mergedTablesList })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Tables merged successfully', type: 'success' })
        await loadTables()
        setShowMergeTablesModal(false)
        setTablesToMerge([])
      } else {
        setToast({ message: data.error || 'Failed to merge tables', type: 'error' })
      }
    } catch (error) {
      console.error('Error merging tables:', error)
      setToast({ message: 'Failed to merge tables', type: 'error' })
    }
  }

  const handleTransferOrder = async () => {
    if (!selectedTable || !selectedTable.currentOrder || !targetTableId) {
      setToast({ message: 'Please select a target table', type: 'error' })
      return
    }

    const targetTable = tables.find(t => t.id === targetTableId)
    if (!targetTable) {
      setToast({ message: 'Target table not found', type: 'error' })
      return
    }

    if (targetTable.status === 'occupied' && targetTable.currentOrder) {
      if (!confirm(`Table ${targetTable.number} already has an order. Transfer anyway?`)) {
        return
      }
    }

    setIsTransferringOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const orderId = selectedTable.currentOrder.id
      const requestBody = {
        target_table_id: targetTableId,
        target_table_number: targetTable.number,
        customer_count: customerCount || 0,
        additional_chairs: additionalChairs || 0
      }
      
      // Send current customer count and additional chairs from state
      // These are the most up-to-date values from the UI
      const response = await fetch(`${apiUrl}/orders/${orderId}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })


      const data = await response.json()

      
      if (data.success) {
        setToast({ message: `Order transferred to Table ${targetTable.number}`, type: 'success' })
        setShowTransferOrderModal(false)
        const savedTargetTableId = targetTableId
        setTargetTableId('')
        
        // Load tables and switch to the target table after transfer
        await loadTables(savedTargetTableId)
      } else {
        console.error('Frontend: Transfer failed:', data.error)
        setToast({ message: data.error || 'Failed to transfer order', type: 'error' })
      }
    } catch (error) {
      console.error('Frontend: Error transferring order:', error)
      setToast({ message: `Failed to transfer order: ${(error as any).message || 'Unknown error'}`, type: 'error' })
    } finally {
      setIsTransferringOrder(false)
    }
  }

  // Check if order can be edited (pending approval)
  const canEditOrder = (order: Order | undefined): boolean => {
    if (!order) return false
    return order.status === 'waiting_for_approval' || order.status === 'draft' || 
           (order.approvalStatus === 'pending_approval')
  }

  // Check if order is approved and can request modification (POS user must approve the request)
  // Waiter can request modification even when order has started preparing; approval is required by POS.
  const canRequestModification = (order: Order | undefined): boolean => {
    if (!order) return false
    
    // Order must be approved (not waiting for approval or draft)
    const isApproved = order.status !== 'waiting_for_approval' && 
                       order.status !== 'draft' && 
                       order.status !== 'modification_requested' &&
                       order.status !== 'cancellation_requested' &&
                       order.status !== 'cancelled'
    
    // Must not already have pending requests
    const hasNoPendingRequests = !order.modificationRequest && !order.cancellationRequest
    
    // Check if approval status is approved or not set (for legacy orders or direct-to-kitchen orders)
    // If order is from waiter and status is 'pending', it means it was approved
    const approvalStatusOk = !order.approvalStatus || 
                             order.approvalStatus === 'approved' ||
                             (order.status === 'pending' && order.approvalStatus !== 'pending_approval')
    
    // Allow modification request even when items are preparing/ready/completed; POS approval is required.
    const canRequest = isApproved && hasNoPendingRequests && approvalStatusOk
    
    return canRequest
  }

  const isValidUUID = (str: string | null | undefined): boolean => {
    if (!str) return false
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(str)
  }

  // Load current order items into cart for modification (use per-line quantities, not summed)
  const loadOrderItemsIntoCart = async (order: Order) => {
    const sourceItems = order?.itemsForModification ?? order?.items
    if (!order || !sourceItems || sourceItems.length === 0) {
      setCart([])
      return
    }

    try {
      const cartItems: CartItem[] = []
      const norm = (s: string) => s.trim().toLowerCase()

      for (const orderItem of sourceItems) {
        const product =
          (orderItem.menuItemId && products.find(p => p.id === orderItem.menuItemId)) ||
          products.find(p => p.name === orderItem.itemName) ||
          products.find(p => norm(p.name) === norm(orderItem.itemName))

        if (product) {
          let variant: MenuVariant | undefined = undefined
          if (orderItem.itemName.includes(' - ')) {
            const variantName = orderItem.itemName.split(' - ')[1]
            variant = product.variants?.find(v => v.name === variantName)
          }

          const categoryPriority = getCategoryPriority(product.categoryId)

          cartItems.push({
            id: orderItem.id || `${Date.now()}-${Math.random()}`,
            name: product.name,
            orderItemNameDb: orderItem.itemName,
            sourceMenuItemId: orderItem.menuItemId,
            basePrice: product.basePrice,
            finalPrice: orderItem.unitPrice,
            product,
            quantity: orderItem.quantity,
            notes: '',
            selectedVariant: variant,
            selectedAddons: [],
            priority: orderItem.priority || 1,
            categoryId: product.categoryId,
            categoryPriority
          })
        } else {
          const stubId =
            orderItem.menuItemId && isValidUUID(orderItem.menuItemId)
              ? orderItem.menuItemId
              : `legacy-line-${orderItem.id}`
          const stubProduct: Product = {
            id: stubId,
            name: orderItem.itemName,
            basePrice: orderItem.unitPrice,
            categoryId: '',
            isActive: true,
            code: orderItem.itemCode,
            description: undefined,
            image: undefined,
            calories: undefined,
            stepsToBurn: undefined,
            allergens: undefined,
            variants: undefined,
            addons: undefined
          }
          console.warn('Product not found for order item — using stub for modification cart:', orderItem.itemName)
          cartItems.push({
            id: orderItem.id || `${Date.now()}-${Math.random()}`,
            name: orderItem.itemName,
            orderItemNameDb: orderItem.itemName,
            sourceMenuItemId: orderItem.menuItemId,
            basePrice: orderItem.unitPrice,
            finalPrice: orderItem.unitPrice,
            product: stubProduct,
            quantity: orderItem.quantity,
            notes: '',
            priority: orderItem.priority || 1,
            categoryId: '',
            categoryPriority: 999
          })
        }
      }

      setCart(cartItems)
    } catch (error) {
      console.error('Error loading order items into cart:', error)
      setToast({ message: 'Failed to load order items', type: 'error' })
    }
  }

  // Handle modification request
  const handleRequestModification = async () => {
    if (!selectedTable?.currentOrder) return

    setIsSubmittingRequest(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      // For now, we'll use a simple modification type - in production, you'd want more sophisticated change tracking
      const requestedChanges = {
        currentItems: selectedTable.currentOrder.itemsForModification ?? selectedTable.currentOrder.items,
        cartItems: cart.map(item => {
          const menuFromProduct = item.product?.id && isValidUUID(item.product.id) ? item.product.id : null
          const menuFromSource = item.sourceMenuItemId && isValidUUID(item.sourceMenuItemId) ? item.sourceMenuItemId : null
          const orderLineId = item.id && isValidUUID(item.id) ? item.id : null
          const itemNameForApi =
            item.orderItemNameDb && String(item.orderItemNameDb).trim()
              ? String(item.orderItemNameDb).trim()
              : item.name
          return {
            order_item_id: orderLineId,
            menu_item_id: menuFromProduct || menuFromSource || null,
            item_code: item.product?.code || null,
            item_name: itemNameForApi,
            quantity: item.quantity,
            unit_price: item.finalPrice
          }
        })
      }

      // Only send requested_by if user.id is a valid UUID, otherwise send null
      const requestedBy = (user?.id && isValidUUID(user.id)) ? user.id : null

      const response = await fetch(`${apiUrl}/orders/${selectedTable.currentOrder.id}/request-modification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requested_by: requestedBy,
          modification_type: 'modify_items',
          requested_changes: requestedChanges,
          reason: modificationReason || null
        })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Modification request submitted successfully', type: 'success' })
        setShowModificationRequestModal(false)
        setModificationReason('')
        await loadTables()
      } else {
        setToast({ message: data.error || 'Failed to submit modification request', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error requesting modification:', error)
      setToast({ message: 'Failed to submit modification request', type: 'error' })
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  // Handle cancellation request
  const handleRequestCancellation = async () => {
    if (!selectedTable?.currentOrder || !cancellationReason) {
      setToast({ message: 'Please select a cancellation reason', type: 'error' })
      return
    }

    setIsSubmittingRequest(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      
      // Only send requested_by if user.id is a valid UUID, otherwise send null
      const requestedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      
      const response = await fetch(`${apiUrl}/orders/${selectedTable.currentOrder.id}/request-cancellation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requested_by: requestedBy,
          cancellation_reason: cancellationReason,
          reason_details: cancellationReasonDetails || null
        })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Cancellation request submitted successfully', type: 'success' })
        setShowCancellationRequestModal(false)
        setCancellationReason('')
        setCancellationReasonDetails('')
        await loadTables()
      } else {
        setToast({ message: data.error || 'Failed to submit cancellation request', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error requesting cancellation:', error)
      setToast({ message: 'Failed to submit cancellation request', type: 'error' })
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  const checkBillRequestStatus = async (orderId: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/bill-requests/pending`)
      const data = await response.json()
      if (data.success && data.data && data.data.length > 0) {
        // Check if there's a pending bill request for this order
        const hasPending = data.data.some((req: any) => req.order_id === orderId && (req.status === 'pending' || req.status === 'processing'))
        setHasPendingBillRequest(hasPending)
      } else {
        setHasPendingBillRequest(false)
      }
    } catch (error) {
      console.error('Error checking bill request status:', error)
      setHasPendingBillRequest(false)
    }
  }

  const handleClearTable = async (tableToClear?: Table) => {
    const table = tableToClear || selectedTable
    if (!table) return

    // Show confirmation modal
    setConfirmModalConfig({
      message: `Are you sure you want to clear table ${table.number}? This will reset the table status, customer count, and additional chairs.`,
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
          const updateData: any = {
            customer_count: 0,
            additional_chairs: 0,
            status: 'available',
            merged_tables: [],
            current_order_id: null  // CRITICAL: Clear the order reference
          }

          const response = await fetch(`${apiUrl}/tables/${table.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData)
          })

          const data = await response.json()
          if (data.success) {
            setToast({ message: `Table ${table.number} cleared successfully`, type: 'success' })
            // Reset local state only if clearing the selected table
            if (!tableToClear || (selectedTable && selectedTable.id === table.id)) {
              setCart([])
              clearSavedCartForTable(table.id) // Clear saved cart for this table
              setCustomerMobile('')
              setSelectedCustomer(null)
              setCustomerCount(0)
              setAdditionalChairs(0)
              setAppliedOffers([])
              setOrderNotes('')
              // Clear the selected table so it doesn't auto-select when back is clicked
              if (selectedTable && selectedTable.id === table.id) {
                setSelectedTable(null)
              }
            }
            // Reload tables to reflect the changes
            await loadTables()
          } else {
            setToast({ message: data.error || 'Failed to clear table', type: 'error' })
          }
        } catch (error) {
          console.error('Error clearing table:', error)
          setToast({ message: 'Failed to clear table', type: 'error' })
        }
      },
      confirmButtonText: 'Clear Table',
      confirmButtonColor: 'red'
    })
    setShowConfirmModal(true)
  }

  const handleRequestBill = async (order?: Order) => {
    const targetOrder = order ?? selectedTable?.currentOrder
    if (!selectedTable || !targetOrder) {
      setToast({ message: 'No order found for this table', type: 'error' })
      return
    }
    if (order == null && hasPendingBillRequest) {
      setToast({ message: 'A bill request is already pending for this order', type: 'error' })
      return
    }

    setIsRequestingBill(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const requestedBy = (user?.id && isValidUUID(user.id)) ? user.id : null

      const response = await fetch(`${apiUrl}/bill-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: targetOrder.id,
          table_id: selectedTable.id,
          table_number: selectedTable.number,
          requested_by: requestedBy,
          notes: null
        })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Bill request sent to POS successfully', type: 'success' })
        if (!order) setHasPendingBillRequest(true)
        await loadTables()
      } else {
        setToast({ message: data.error || 'Failed to request bill', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error requesting bill:', error)
      setToast({ message: 'Failed to request bill', type: 'error' })
    } finally {
      setIsRequestingBill(false)
    }
  }

  // Update draft order (when status is waiting_for_approval)
  const handleUpdateDraftOrder = async () => {
    if (!selectedTable?.currentOrder || !canEditOrder(selectedTable.currentOrder)) {
      setToast({ message: 'Order cannot be edited', type: 'error' })
      return
    }

    if (cart.length === 0) {
      setToast({ message: 'Cart is empty', type: 'error' })
      return
    }

    setIsPlacingOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const draftItems = cart.map(item => ({
        menu_item_id: item.product.id,
        item_code: item.product.code || null,
        item_name: item.name,
        base_price: item.basePrice,
        variant_name: item.selectedVariant?.name,
        variant_price_modifier: item.selectedVariant?.priceModifier || 0,
        addons: item.selectedAddons?.map(addon => ({
          id: addon.id,
          name: addon.name,
          price: addon.price,
          category: addon.category
        })) || [],
        quantity: item.quantity,
        unit_price: item.finalPrice,
        total_price: item.finalPrice * item.quantity,
        notes: item.notes,
        priority: item.priority
      }))

      const updateData = {
        draft_items: draftItems,
        subtotal: getSubtotal(),
        tax_amount: getTax(),
        service_charge: getServiceCharge(),
        discount_amount: getDiscountAmount(),
        total_amount: getTotal(),
        notes: orderNotes,
        customer_name: selectedCustomer?.name || selectedCustomer?.first_name || 'Guest',
        customer_phone: customerMobile || selectedCustomer?.mobile_number || selectedCustomer?.phone,
        customer_id: selectedCustomer?.id,
        customer_count: customerCount,
        additional_chairs: additionalChairs
      }

      const response = await fetch(`${apiUrl}/orders/drafts/${selectedTable.currentOrder.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Order updated successfully', type: 'success' })
        setCart([])
        if (selectedTable) clearSavedCartForTable(selectedTable.id) // Clear saved cart on successful order update
        setOrderNotes('')
        setAppliedOffers([])
        await loadTables()
      } else {
        setToast({ message: data.error || 'Failed to update order', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error updating draft order:', error)
      setToast({ message: 'Failed to update order', type: 'error' })
    } finally {
      setIsPlacingOrder(false)
    }
  }

  const handlePlaceOrder = async () => {
    // Check if customer count is required
    if (restaurantSettings?.waiter_require_customer_count && (!customerCount || customerCount <= 0)) {
      // Open table settings modal to allow waiter to add customer count
      setPendingPlaceOrder(true)
      setShowTableSettingsModal(true)
      return
    }
    
    await processOrderPlacement()
  }

  const processOrderPlacement = async () => {
    if (!selectedTable) {
      setToast({ message: 'Please select a table', type: 'error' })
      return
    }

    if (cart.length === 0) {
      setToast({ message: 'Cart is empty', type: 'error' })
      return
    }

    // Check if cash register is open
    try {
      const registerResponse = await apiService.getCashRegisterStatus()
      const isOpen = (registerResponse as any).isOpen ?? (registerResponse.data as any)?.isOpen ?? false
      if (!registerResponse.success || !isOpen) {
        setToast({ message: 'Cash register is not open. Please open the cash register in POS before placing orders.', type: 'error' })
        return
      }
    } catch (error) {
      setToast({ message: 'Cash register is not open. Please open the cash register in POS before placing orders.', type: 'error' })
      return
    }

    setIsPlacingOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      
      // Check if table has active order (not completed)
      const hasActiveOrder = selectedTable.currentOrder && 
                            selectedTable.currentOrder.status !== 'completed' &&
                            selectedTable.currentOrder.status !== 'cancelled'
      
      if (hasActiveOrder && selectedTable.currentOrder) {
        const orderItems = cart.map(item => ({
          menu_item_id: item.product.id,
          item_code: item.product.code || null,
          item_name: item.name,
          base_price: item.basePrice,
          variant_name: item.selectedVariant?.name,
          variant_price_modifier: item.selectedVariant?.priceModifier || 0,
          addons: item.selectedAddons?.map(addon => ({
            id: addon.id,
            name: addon.name,
            price: addon.price,
            category: addon.category
          })) || [],
          quantity: item.quantity,
          unit_price: item.finalPrice,
          total_price: item.finalPrice * item.quantity,
          notes: item.notes,
          priority: item.priority
        }))

        const response = await fetch(`${apiUrl}/orders/${selectedTable.currentOrder.id}/add-items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: orderItems, notes: orderNotes })
        })

        const data = await response.json()
        
        if (!data.success && data.error && (data.error.includes('completed order') || data.error.includes('Cannot add items'))) {
          // Fall through to create new order
        } else if (data.success) {
          await refreshSingleTableSnapshot(selectedTable.id)
          if (data.requires_approval) {
            setToast({ message: 'Additional items submitted for approval. Waiting for POS approval.', type: 'success' })
          } else {
            setToast({ message: 'Items added to order successfully', type: 'success' })
          }
          setCart([])
          clearSavedCartForTable(selectedTable.id) // Clear saved cart on successful order
          setOrderNotes('')
          setHasPendingBillRequest(false)
          void loadTables()
          return
        } else {
          setToast({ message: data.error || 'Failed to add items to order', type: 'error' })
          return
        }
      }
      
      // Create new order (or add to completed order failed)
      {
        const orderData = {
          order_type: 'dine_in',
          table_number: selectedTable.number,
          table_id: selectedTable.id,
          customer_name: selectedCustomer?.name || selectedCustomer?.first_name || 'Guest',
          customer_phone: customerMobile || selectedCustomer?.mobile_number || selectedCustomer?.phone,
          customer_id: selectedCustomer?.id,
          items: cart.map(item => ({
            menu_item_id: item.product.id,
            item_code: item.product.code || null,
            item_name: item.name,
            base_price: item.basePrice,
            variant_name: item.selectedVariant?.name,
            variant_price_modifier: item.selectedVariant?.priceModifier || 0,
            addons: item.selectedAddons?.map(addon => ({
              id: addon.id,
              name: addon.name,
              price: addon.price,
              category: addon.category
            })) || [],
            quantity: item.quantity,
            unit_price: item.finalPrice,
            total_price: item.finalPrice * item.quantity,
            notes: item.notes,
            priority: item.priority,
            is_combo_item: item.isComboItem || false,
            combo_price: item.comboPrice,
            original_price: item.originalPrice,
            is_bogo_item: item.isBOGOItem || false,
            bogo_discount_type: item.bogoDiscountType,
            bogo_discount_value: item.bogoDiscountValue,
            paid_quantity: item.paidQuantity,
            free_quantity: item.freeQuantity,
            is_free_item: item.isFreeItem || false
          })),
          subtotal: getSubtotal(),
          tax_amount: getTax(),
          service_charge: getServiceCharge(),
          discount_amount: getDiscountAmount(),
          total_amount: getTotal(),
          notes: orderNotes,
          order_source: 'waiter',
          applied_offers: appliedOffers.map(offer => ({
            id: offer.id,
            name: offer.name,
            type: offer.type,
            value: offer.value
          }))
        }

        // Use apiService so Authorization (pos-auth-token) is sent; backend sets created_by → placed_by_name
        const data = await apiService.createOrder(orderData)

        if (data.success) {
          const isDraft = data.isDraft || data.data?.status === 'waiting_for_approval' || data.data?.status === 'draft'
          const requiresApproval = data.requires_approval || isDraft
          
          if (requiresApproval) {
            setToast({ message: 'Order submitted for approval. Waiting for POS approval.', type: 'success' })
          } else {
            const tokenNumber = data.data?.token_number ?? null
            if (tokenNumber !== null && !isNaN(tokenNumber)) {
              setLastOrderToken(tokenNumber)
              setToast({ message: `Order placed successfully. Token #${tokenNumber}`, type: 'success' })
            } else {
              setToast({ message: 'Order placed successfully', type: 'success' })
            }
          }
          
          setCart([])
          clearSavedCartForTable(selectedTable.id) // Clear saved cart on successful order
          setOrderNotes('')
          setAppliedOffers([])
          await loadTables(selectedTable.id)
        } else {
          setToast({ message: data.error || 'Failed to place order', type: 'error' })
        }
      }
    } catch (error) {
      console.error('Error placing order:', error)
      setToast({ message: 'Failed to place order', type: 'error' })
    } finally {
      setIsPlacingOrder(false)
    }
  }

  // Helper function to get all category IDs including subcategories
  const getCategoryAndSubcategoryIds = (categoryId: string): string[] => {
    if (categoryId === 'all') return []
    
    const category = getAllCategoriesFlat().find(c => c.id === categoryId)
    if (!category) return [categoryId]
    
    // Get all subcategory IDs
    const subcategoryIds = category.children?.map(child => child.id) || []
    
    // Return the main category ID plus all subcategory IDs
    return [categoryId, ...subcategoryIds]
  }

  const filteredProducts = useMemo(() => {
    let list = products.filter(p => p.isActive)
    if (selectedCategoryFilter !== 'all') {
      const categoryIds = getCategoryAndSubcategoryIds(selectedCategoryFilter)
      list = list.filter(p => categoryIds.includes(p.categoryId))
    }
    if (productSearchTerm.trim()) {
      const searchLower = productSearchTerm.toLowerCase()
      list = list.filter(p =>
        p.code?.toLowerCase().includes(searchLower) ||
        p.name.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower)
      )
    }
    return [...list].sort((a, b) => compareProductCode(a.code, b.code))
  }, [products, selectedCategoryFilter, productSearchTerm, getAllCategoriesFlat])

  const sortedCart = useMemo(() => {
    return [...cart].sort((a, b) => a.priority - b.priority)
  }, [cart])

  const filteredTables = useMemo(() => {
    let filtered = tables
    if (tableSearchTerm) {
      filtered = filtered.filter(table => 
        table.number.toLowerCase().includes(tableSearchTerm.toLowerCase())
      )
    }
    return filtered
  }, [tables, tableSearchTerm])

  const getFloorPlanName = (floorPlanId?: string) => {
    if (!floorPlanId) return 'No Floor'
    const floorPlan = floorPlans.find(fp => fp.id === floorPlanId)
    return floorPlan ? floorPlan.name : 'Unknown'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  // Respect POS setting to completely enable/disable waiter module
  const waiterEnabled = posSettings?.enableWaiterModule ?? true

  if (!waiterEnabled) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Waiter Module Disabled</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            The waiter module is currently turned off in POS Settings. Please contact your administrator if you need access.
          </p>
          <a
            href="/app"
            className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-md"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 w-full min-h-0 bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Single fullscreen container so switching list ↔ table does not exit fullscreen */}
      <div ref={fullscreenRef} className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
      {/* Expanded Tables View */}
      {!selectedTable && (
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 overflow-hidden w-full">
          {/* Compact Header: Floor filter + Search + Fullscreen */}
          <div className="px-3 lg:px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Floor Plan Filter - inline */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 hidden sm:inline">Floor:</span>
                <button
                  onClick={() => setSelectedFloorPlanId('all')}
                  className={`px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    selectedFloorPlanId === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  All
                </button>
                {floorPlans.map(fp => (
                  <button
                    key={fp.id}
                    onClick={() => setSelectedFloorPlanId(fp.id)}
                    className={`px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      selectedFloorPlanId === fp.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {fp.name}
                  </button>
                ))}
              </div>

              {/* Table Search - compact */}
              <div className="relative flex-1 min-w-0 max-w-xs">
                <MagnifyingGlassIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tables..."
                  value={tableSearchTerm}
                  onChange={(e) => setTableSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Fullscreen */}
              <button
                type="button"
                onClick={toggleFullScreen}
                title={isFullScreen ? 'Exit full screen' : 'Full screen'}
                aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
                className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 flex-shrink-0"
              >
                {isFullScreen ? (
                  <ArrowsPointingInIcon className="h-5 w-5" />
                ) : (
                  <ArrowsPointingOutIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Expanded Table Grid */}
          <div className="flex-1 overflow-y-auto px-2 py-4 lg:px-4 w-full min-h-0">
            {filteredTables.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <TableCellsIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No tables found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {tableSearchTerm ? 'Try adjusting your search criteria' : 'No tables available for the selected floor'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-full items-stretch">
                {filteredTables.map((table: Table) => {
                  const isSelected = selectedTable !== null && (selectedTable as Table).id === table.id
                  return (
                    <div
                      key={table.id}
                      onClick={async () => {
                        // Save current cart for the previous table before switching
                        const currentTable = selectedTable as Table | null
                        if (currentTable && currentTable.id !== table.id && cart.length > 0) {
                          // Only save if the table doesn't have an active order (cart is for new orders)
                          if (!currentTable.currentOrder || currentTable.currentOrder.status === 'completed' || currentTable.currentOrder.status === 'cancelled') {
                            saveCartForTable(currentTable.id)
                          }
                        }
                        
                        // Clear cart before loading new table data
                        setCart([])
                        
                        // Fetch latest table data to ensure we have the most recent order info
                        try {
                          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
                          const response = await fetch(`${apiUrl}/tables/${table.id}`)
                          const data = await response.json()
                          if (data.success && data.data) {
                            const updatedTable = data.data
                            // Transform the table data the same way as in loadTables
                            const transformedTable: Table = {
                              id: updatedTable.id,
                              number: updatedTable.number,
                              capacity: updatedTable.capacity,
                              status: updatedTable.status || 'available',
                              location: (updatedTable.location || 'indoor') as 'indoor' | 'outdoor' | 'bar',
                              floor_plan_id: updatedTable.floor_plan_id,
                              customerCount: updatedTable.customer_count || 0,
                              additionalChairs: updatedTable.additional_chairs || 0,
                              mergedTables: updatedTable.merged_tables || [],
                              updated_at: updatedTable.updated_at,
                              // CRITICAL: Only attach non-completed orders, OR completed orders if table is occupied (for invoice requests)
                              currentOrder: updatedTable.current_order && 
                                (updatedTable.current_order.status !== 'completed' || 
                                 (updatedTable.status === 'occupied' || updatedTable.status === 'waiting_for_approval')) ? {
                                id: updatedTable.current_order.id,
                                orderNumber: updatedTable.current_order.order_number,
                                tableId: updatedTable.id,
                                customerName: updatedTable.current_order.customer_name,
                                customerPhone: updatedTable.current_order.customer_phone,
                                customerId: updatedTable.current_order.customer_id,
                                status: updatedTable.current_order.status,
                                approvalStatus: updatedTable.current_order.approval_status,
                                total: parseFloat(updatedTable.current_order.total || updatedTable.current_order.total_amount || 0),
                                subtotal: parseFloat(updatedTable.current_order.subtotal || 0),
                                taxAmount: parseFloat(updatedTable.current_order.tax_amount || 0),
                                serviceCharge: parseFloat(updatedTable.current_order.service_charge || 0),
                                discountAmount: parseFloat(updatedTable.current_order.discount_amount || 0),
                                items: (updatedTable.current_order.order_items || []).map((item: any) => ({
                                  id: item.id,
                                  itemName: item.item_name,
                                  quantity: item.quantity,
                                  unitPrice: parseFloat(item.unit_price || 0),
                                  totalPrice: parseFloat(item.total_price || 0),
                                  status: item.status || 'pending',
                                  priority: item.priority,
                                  menuItemId: item.menu_item_id || undefined,
                                  itemCode: item.item_code || undefined
                                })),
                                itemsForModification: (updatedTable.current_order.order_items_for_modification || updatedTable.current_order.order_items || []).map((item: any) => ({
                                  id: item.id,
                                  itemName: item.item_name,
                                  quantity: item.quantity,
                                  unitPrice: parseFloat(item.unit_price || 0),
                                  totalPrice: parseFloat(item.total_price || 0),
                                  status: item.status || 'pending',
                                  priority: item.priority,
                                  menuItemId: item.menu_item_id || undefined,
                                  itemCode: item.item_code || undefined
                                })),
                                createdAt: updatedTable.current_order.created_at,
                                modificationRequest: updatedTable.current_order.order_modification_requests?.[0] ? {
                                  id: updatedTable.current_order.order_modification_requests[0].id,
                                  modificationType: updatedTable.current_order.order_modification_requests[0].modification_type,
                                  requestedChanges: updatedTable.current_order.order_modification_requests[0].requested_changes,
                                  reason: updatedTable.current_order.order_modification_requests[0].reason,
                                  approvalStatus: updatedTable.current_order.order_modification_requests[0].approval_status,
                                  requestedAt: updatedTable.current_order.order_modification_requests[0].requested_at
                                } : undefined,
                                cancellationRequest: updatedTable.current_order.order_cancellation_requests?.[0] ? {
                                  id: updatedTable.current_order.order_cancellation_requests[0].id,
                                  cancellationReason: updatedTable.current_order.order_cancellation_requests[0].cancellation_reason,
                                  reasonDetails: updatedTable.current_order.order_cancellation_requests[0].reason_details,
                                  approvalStatus: updatedTable.current_order.order_cancellation_requests[0].approval_status,
                                  requestedAt: updatedTable.current_order.order_cancellation_requests[0].requested_at
                                } : undefined,
                                pendingAdditionalItems: updatedTable.current_order.pending_additional_items || undefined,
                                pendingAdditionalItemsStatus: updatedTable.current_order.pending_additional_items_status || undefined
                              } : undefined,
                              reservation: updatedTable.reservation ? {
                                id: updatedTable.reservation.id,
                                customerName: updatedTable.reservation.customer_name,
                                customerPhone: updatedTable.reservation.customer_phone,
                                partySize: updatedTable.reservation.party_size,
                                reservationTime: updatedTable.reservation.reservation_time,
                                status: updatedTable.reservation.status
                              } : undefined
                            }
                            setSelectedTable(transformedTable)
                            
                            // Restore saved cart if table doesn't have an active order
                            if (!transformedTable.currentOrder || 
                                transformedTable.currentOrder.status === 'completed' || 
                                transformedTable.currentOrder.status === 'cancelled') {
                              restoreCartForTable(table.id)
                            }
                            // Debug logging commented out - uncomment for debugging if needed
                            // console.log('✅ Selected table with refreshed data:', {
                            //   tableId: transformedTable.id,
                            //   tableNumber: transformedTable.number,
                            //   hasCurrentOrder: !!transformedTable.currentOrder,
                            //   orderStatus: transformedTable.currentOrder?.status
                            // })
                          } else {
                            // Fallback to table from list if single fetch fails
                            setSelectedTable(table)
                            // Restore saved cart for fallback case
                            restoreCartForTable(table.id)
                          }
                        } catch (error) {
                          console.error('Error fetching table data:', error)
                          // Fallback to table from list if fetch fails
                          setSelectedTable(table)
                          // Restore saved cart for error case
                          restoreCartForTable(table.id)
                        }
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 transform hover:scale-105 flex flex-col h-full ${
                        isSelected
                          ? 'bg-primary-100 dark:bg-primary-900 border-2 border-primary-500 shadow-lg'
                          : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-500 shadow-md hover:shadow-lg'
                      }`}
                    >
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{table.number}</h3>
                        {/* Saved cart indicator */}
                        {tableCarts[table.id] && tableCarts[table.id].length > 0 && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 text-xs font-medium" title={`${tableCarts[table.id].length} item(s) in saved cart`}>
                            <ShoppingCartIcon className="h-3 w-3" />
                            {tableCarts[table.id].length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleClearTable(table)
                          }}
                          className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
                          title="Clear table"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${
                          table.status === 'available' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          table.status === 'occupied' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          table.status === 'reserved' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          table.status === 'waiting_for_approval' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {table.status === 'waiting_for_approval' ? 'Waiting for Approval' : table.status.charAt(0).toUpperCase() + table.status.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1.5 flex-1 flex flex-col">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 flex-shrink-0">
                        <UserGroupIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="font-medium">Capacity: {table.capacity}</span>
                        {table.additionalChairs && table.additionalChairs > 0 && (
                          <span className="text-primary-600 dark:text-primary-400 font-semibold whitespace-nowrap">
                            (+{table.additionalChairs})
                          </span>
                        )}
                      </div>
                      {table.customerCount && table.customerCount > 0 && (
                        <div className="text-xs font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded flex-shrink-0">
                          👥 {table.customerCount} guests
                        </div>
                      )}
                      {table.currentOrder && (
                        <div className="text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded flex-shrink-0 break-words">
                          📋 {table.currentOrder.orderNumber || `ORD-${table.currentOrder.id.slice(0, 8).toUpperCase()}`}
                        </div>
                      )}
                      {table.status === 'occupied' && (table.currentOrder || table.customerCount) && (
                        <div className="text-xs font-semibold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded flex-shrink-0 flex items-center gap-1">
                          <ClockIcon className="h-3 w-3" />
                          <span>
                            {table.currentOrder 
                              ? formatElapsedTime(table.currentOrder.createdAt)
                              : table.updated_at 
                                ? formatElapsedTime(table.updated_at)
                                : 'Just now'}
                          </span>
                        </div>
                      )}
                      <div className="mt-auto pt-1">
                        {table.floor_plan_id && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 flex-shrink-0">
                            <BuildingOfficeIcon className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{getFloorPlanName(table.floor_plan_id)}</span>
                          </div>
                        )}
                        {table.mergedTables && table.mergedTables.length > 0 && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded mt-1 flex-shrink-0">
                            🔗 Merged ({table.mergedTables.length})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content - min-h-0 so Products+Cart row gets bounded height and Place Order stays visible */}
      {selectedTable && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <>
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-6 py-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedTable.number}
                    </h1>
                    {selectedTable.mergedTables && selectedTable.mergedTables.length > 0 && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                        Merged with {selectedTable.mergedTables.length} table(s)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <span>Capacity: {selectedTable.capacity + (additionalChairs || 0)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 relative">
                  {selectedCustomer && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 h-9 bg-green-100 dark:bg-green-900 rounded-lg">
                      <UserIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <div className="flex flex-col leading-tight">
                        <span className="text-xs font-medium text-green-800 dark:text-green-200">
                          Registered Customer
                        </span>
                        {customerVisitCount !== null && customerVisitCount > 0 && (
                          <span className="text-[10px] text-green-600 dark:text-green-400">
                            {customerMobile} • {customerVisitCount} {customerVisitCount === 1 ? 'visit' : 'visits'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {isSearchingCustomer && (
                    <div className="text-sm text-gray-500">Searching...</div>
                  )}
                  <div className="relative">
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Customer mobile number"
                      value={customerMobile}
                      onClick={() => setShowMobileKeypad(true)}
                      onFocus={() => setShowMobileKeypad(true)}
                      onChange={(e) => {
                        // Only allow numbers
                        const value = e.target.value.replace(/[^0-9]/g, '')
                        setCustomerMobile(value)
                        if (value.length >= 10) {
                          handleCustomerSearch(value)
                          setShowMobileKeypad(false) // Auto-close pinpad when valid number is entered
                        }
                      }}
                      className="w-48 pl-3 pr-10 py-1.5 h-9 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      readOnly
                    />
                    {customerMobile && (
                      <button
                        onClick={handleClearCustomerMobile}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        type="button"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {showMobileKeypad && (
                    <div ref={keypadRef} className="absolute top-full left-0 mt-2 z-50 w-64">
                      <NumericKeypad
                        value={customerMobile}
                        onChange={(value) => {
                          setCustomerMobile(value)
                          if (value.length >= 10) {
                            handleCustomerSearch(value)
                            setShowMobileKeypad(false) // Auto-close pinpad when valid number is entered
                          }
                        }}
                        onClose={() => setShowMobileKeypad(false)}
                        maxLength={15}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => {
                      // Save cart for current table before going back (only if no active order)
                      if (selectedTable && cart.length > 0 && 
                          (!selectedTable.currentOrder || 
                           selectedTable.currentOrder.status === 'completed' || 
                           selectedTable.currentOrder.status === 'cancelled')) {
                        saveCartForTable(selectedTable.id)
                      }
                      // Go back to table selection
                      setSelectedTable(null)
                    }}
                    className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 flex items-center gap-2"
                    title="Back to table selection"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={toggleFullScreen}
                    className="p-2 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 flex items-center justify-center"
                    title={isFullScreen ? 'Exit full screen' : 'Full screen'}
                    aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
                  >
                    {isFullScreen ? (
                      <ArrowsPointingInIcon className="h-5 w-5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setScanQRError(null)
                      setShowScanQRModal(true)
                    }}
                    className="p-2 text-sm bg-indigo-100 dark:bg-indigo-900/50 hover:bg-indigo-200 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200 rounded-lg flex items-center justify-center"
                    title="Scan customer order QR"
                    aria-label="Scan customer order QR"
                  >
                    <QrCodeIcon className="h-5 w-5" />
                  </button>
                  {/* Clear Table button - visible to Super Admin and Admin when table is occupied but NO active order */}
                  {(user?.roles?.some(r => r.name === 'Super Admin' || r.name === 'Admin')) && 
                   !selectedTable?.currentOrder &&
                   (selectedTable?.status === 'occupied' || ((selectedTable?.customerCount ?? 0) > 0)) && (
                    <button
                      onClick={() => handleClearTable()}
                      className="p-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center"
                      title="Clear table (Super Admin/Admin only)"
                      aria-label="Clear table"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                  {!selectedTable?.currentOrder && (
                    <button
                      onClick={async () => {
                      // Reset everything in local state
                      setCart([])
                      setCustomerMobile('')
                      setSelectedCustomer(null)
                      setCustomerCount(0)
                      setAdditionalChairs(0)
                      setAppliedOffers([])
                      setOrderNotes('')
                      setItemNotes('')
                      setSelectedProduct(null)
                      setSelectedVariant(null)
                      setSelectedAddons([])
                      
                      // If a table is selected, also update it in the database
                      if (selectedTable) {
                        const currentTableId = selectedTable.id
                        try {
                          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
                          const updateData: any = {
                            customer_count: 0,
                            additional_chairs: 0,
                            status: 'available' // Reset status to available
                          }

                          const response = await fetch(`${apiUrl}/tables/${currentTableId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(updateData)
                          })

                          const data = await response.json()
                          if (data.success) {
                            // Reload tables to reflect the changes
                            await loadTables()
                            // Clear the selected table so it doesn't auto-select when back is clicked
                            setSelectedTable(null)
                          }
                        } catch (error) {
                          console.error('Error resetting table settings:', error)
                        }
                      }
                    }}
                    className="p-2 text-sm bg-red-200 dark:bg-red-700 hover:bg-red-300 dark:hover:bg-red-600 rounded-lg text-red-700 dark:text-red-300 flex items-center justify-center"
                    title="Reset all fields"
                    aria-label="Reset all fields"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                  </button>
                  )}
                  <button
                    onClick={() => setShowTableSettingsModal(true)}
                    className="p-2 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 flex items-center justify-center"
                    title="Table settings"
                    aria-label="Table settings"
                  >
                    <CogIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Order Status */}
              {selectedTable.currentOrder && (
                <div className="mt-3 flex items-center justify-end">
                  <div className="flex-shrink-0 px-4 py-2 bg-blue-50 dark:bg-blue-900 rounded-lg">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
                            {selectedTable.currentOrder.orderNumber || `ORD-${selectedTable.currentOrder.id.slice(0, 8).toUpperCase()}`}
                          </div>
                          {(selectedTable.currentOrder as any).token_number && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/80 dark:bg-blue-700 text-xs font-semibold text-blue-800 dark:text-blue-100 border border-blue-200 dark:border-blue-600">
                              Token #{(selectedTable.currentOrder as any).token_number}
                            </span>
                          )}
                          {/* Order Status Badge */}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            selectedTable.currentOrder.status === 'waiting_for_approval' || selectedTable.currentOrder.status === 'draft' 
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : selectedTable.currentOrder.status === 'modification_requested'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                              : selectedTable.currentOrder.status === 'cancellation_requested'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : selectedTable.currentOrder.status === 'pending'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : selectedTable.currentOrder.status === 'preparing'
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                              : selectedTable.currentOrder.status === 'ready'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : selectedTable.currentOrder.status === 'completed'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {selectedTable.currentOrder.status === 'waiting_for_approval' || selectedTable.currentOrder.status === 'draft'
                              ? 'Waiting for Approval'
                              : selectedTable.currentOrder.status === 'modification_requested'
                              ? 'Modification Requested'
                              : selectedTable.currentOrder.status === 'cancellation_requested'
                              ? 'Cancellation Requested'
                              : selectedTable.currentOrder.status.charAt(0).toUpperCase() + selectedTable.currentOrder.status.slice(1)}
                          </span>
                          {selectedTable.currentOrder.pendingAdditionalItemsStatus === 'pending_approval' && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                              Additional Items Pending Approval
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-blue-700 dark:text-blue-300">
                          {(() => {
                            const displayItems = selectedTable.currentOrder.itemsForModification ?? selectedTable.currentOrder.items
                            const itemsTotal = displayItems.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0)
                            const discount = Number(selectedTable.currentOrder.discountAmount) || 0
                            const serviceCharge = Number(selectedTable.currentOrder.serviceCharge) || 0
                            const total = Math.max(0, itemsTotal - discount) + serviceCharge
                            const symbol = restaurantSettings?.currency_symbol || 'SAR'
                            return `${displayItems.length} item${displayItems.length !== 1 ? 's' : ''} • ${symbol} ${total.toFixed(2)}`
                          })()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowOrderDetailsModal(true)}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          title="View order details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {(() => {
          // Normalize status to handle case sensitivity and whitespace
          const normalizedStatus = (selectedTable.currentOrder.status || '').toLowerCase().trim()
          
          const canShowBillButton = 
            normalizedStatus !== 'waiting_for_approval' && 
            normalizedStatus !== 'draft' && 
            normalizedStatus !== 'cancelled' && 
            !hasPendingBillRequest
          
          // Debug logging removed to prevent excessive console output
          // Uncomment below for debugging if needed:
          // console.log('Request Bill button check:', {
          //   rawStatus: selectedTable.currentOrder.status,
          //   normalizedStatus: normalizedStatus,
          //   canShow: canShowBillButton,
          //   isWaitingForApproval: normalizedStatus === 'waiting_for_approval',
          //   isDraft: normalizedStatus === 'draft',
          //   isCancelled: normalizedStatus === 'cancelled',
          //   hasPendingBillRequest: hasPendingBillRequest,
          //   orderId: selectedTable.currentOrder.id
          // })
                          
                          return canShowBillButton ? (
                            <button
                              onClick={() => handleRequestBill(selectedTable?.currentOrder)}
                              disabled={isRequestingBill}
                              className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                              title="Request bill"
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                            </button>
                          ) : null
                        })()}
                        {hasPendingBillRequest && (
                          <div className="p-2 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-lg flex items-center gap-2" title="Bill request pending">
                            <DocumentTextIcon className="h-4 w-4" />
                            <span className="text-xs font-medium">Bill Requested</span>
                          </div>
                        )}
                        {canRequestModification(selectedTable.currentOrder) && (
                          <>
                            <button
                              onClick={async () => {
                                // Load current order items into cart before opening modal
                                if (selectedTable.currentOrder) {
                                  await loadOrderItemsIntoCart(selectedTable.currentOrder)
                                }
                                setShowModificationRequestModal(true)
                              }}
                              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
                              title="Request modification"
                            >
                              Modify
                            </button>
                            <button
                              onClick={() => setShowCancellationRequestModal(true)}
                              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
                              title="Request cancellation"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {selectedTable.currentOrder.status !== 'waiting_for_approval' && selectedTable.currentOrder.status !== 'draft' && (
                          <button
                            onClick={() => setShowTransferOrderModal(true)}
                            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            title="Transfer order to another table"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Products and Cart - flex-1 min-h-0 so cart sidebar height is bounded */}
            <div className="flex-1 flex min-h-0 overflow-hidden bg-gray-50 dark:bg-gray-900">
              {/* Products Grid */}
              <div className="flex-1 overflow-y-auto min-w-0 min-h-0 bg-gray-50 dark:bg-gray-900">
                {/* Category Filter - Sticky, compact */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 pt-1.5 px-3 pb-1.5 mb-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex gap-1.5 overflow-x-auto thin-scrollbar">
                  <button
                    onClick={() => setSelectedCategoryFilter('all')}
                    className={`px-2.5 py-1 rounded-md text-sm whitespace-nowrap ${
                      selectedCategoryFilter === 'all'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    All
                  </button>
                  {categories.filter(cat => !cat.parentId && cat.isActive).map((category: any) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategoryFilter(category.id)}
                      className={`px-2.5 py-1 rounded-md text-sm whitespace-nowrap ${
                        selectedCategoryFilter === category.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                  </div>
                  <div className="mt-2 relative">
                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      className="w-full py-1.5 pl-8 pr-8 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {productSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setProductSearchTerm('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        aria-label="Clear search"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Products Grid */}
                <div className="px-4 pb-4 bg-gray-50 dark:bg-gray-900">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredProducts.map(product => (
                    <div
                      key={product.id}
                      onClick={() => handleAddToCart(product)}
                      className="relative bg-white dark:bg-gray-800 rounded-md shadow hover:shadow-md transition-shadow cursor-pointer p-3"
                    >
                      {product.code && (
                        <div className="absolute top-1.5 left-1.5 bg-yellow-500 text-white text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded z-10">
                          {product.code}
                        </div>
                      )}
                      {product.image && (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-24 object-cover rounded mb-1.5"
                        />
                      )}
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5 line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-1">
                        {product.description}
                      </p>
                      {(product.calories != null || product.stepsToBurn != null) && (
                        <div className="flex items-center justify-between gap-1 mb-1 text-[10px] text-gray-500 dark:text-gray-400 min-h-[1rem]">
                          {product.calories != null && (
                            <span className="flex items-center shrink-0">
                              <FireIcon className="h-3 w-3 mr-0.5 shrink-0 text-orange-500" aria-hidden />
                              {product.calories} cal
                            </span>
                          )}
                          {product.stepsToBurn != null && (
                            <span className="flex items-center shrink-0 ml-auto">
                              <span className="mr-0.5 text-xs leading-none select-none" aria-hidden>🚶‍♀️</span>
                              <span>{product.stepsToBurn} steps</span>
                            </span>
                          )}
                        </div>
                      )}
                      {product.allergens && product.allergens.length > 0 && (
                        <div className="mb-1">
                          <AllergenIcons allergenIds={product.allergens} allergenMap={allergenMap} size={16} />
                        </div>
                      )}
                      <p className="text-sm font-bold text-primary-600 dark:text-primary-400">
                        {restaurantSettings?.currency_symbol || 'SAR'} {product.basePrice.toFixed(2)}
                      </p>
                    </div>
                  ))}
                  </div>
                </div>
              </div>

              {/* Cart Sidebar - fixed height from parent so Place Order is always visible without scroll */}
              <div className="w-80 h-full max-h-full min-h-0 flex flex-col flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Cart</h2>
                  {selectedTable.currentOrder && selectedTable.currentOrder.status !== 'completed' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Adding to existing order
                    </p>
                  )}
                  <input
                    ref={scanQRInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleScanQRFile}
                  />
                </div>

                <div
                  className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-2 min-h-0"
                  onDragOver={handleDragOver}
                >
                  {sortedCart.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                      Cart is empty
                    </div>
                  ) : (
                    sortedCart.map((item) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item.id)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, item.id)}
                        onDragEnd={handleDragEnd}
                        className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-3 cursor-move ${
                          draggedItem === item.id ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-1 cursor-grab active:cursor-grabbing">
                            <Bars3Icon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {item.name}
                              </h4>
                              <button
                                onClick={() => handleRemoveFromCart(item.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                            {item.selectedVariant && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                Variant: {item.selectedVariant.name}
                              </p>
                            )}
                            {item.selectedAddons && item.selectedAddons.length > 0 && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                Addons: {item.selectedAddons.map(a => a.name).join(', ')}
                              </p>
                            )}
                            {(() => {
                              const ids = [...(item.product.allergens || []), ...(item.selectedVariant?.allergen_ids || []), ...(item.selectedAddons || []).flatMap(a => a.allergen_ids || [])]
                              const combined = [...new Set(ids)]
                              return combined.length > 0 ? (
                                <div className="mt-1">
                                  <AllergenIcons allergenIds={combined} allergenMap={allergenMap} size={14} />
                                </div>
                              ) : null
                            })()}
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleUpdateQuantity(item.id, -1)}
                                  className="p-1 rounded bg-gray-200 dark:bg-gray-600"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                                <span className="text-sm font-medium">{item.quantity}</span>
                                <button
                                  onClick={() => handleUpdateQuantity(item.id, 1)}
                                  className="p-1 rounded bg-gray-200 dark:bg-gray-600"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="text-right">
                                {item.discountOfferId && item.discountOriginalPrice ? (
                                  <div>
                                    <p className="text-sm font-bold text-green-600 dark:text-green-400">
                                      {restaurantSettings?.currency_symbol || 'SAR'} {(item.finalPrice * item.quantity).toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-400 line-through">
                                      {restaurantSettings?.currency_symbol || 'SAR'} {(item.discountOriginalPrice * item.quantity).toFixed(2)}
                                    </p>
                                  </div>
                                ) : item.isBOGOItem && item.paidQuantity !== undefined && item.freeQuantity !== undefined ? (
                                  <div>
                                    {/* For BOGO: discounted price = price of paid quantity, original = price of total quantity */}
                                    {(() => {
                                      const unitPrice = item.originalPrice || item.basePrice || item.finalPrice
                                      const paidTotal = unitPrice * (item.paidQuantity || 0)
                                      const originalTotal = unitPrice * item.quantity
                                      return (
                                        <>
                                          <p className="text-sm font-bold text-green-600 dark:text-green-400">
                                            {restaurantSettings?.currency_symbol || 'SAR'} {paidTotal.toFixed(2)}
                                          </p>
                                          <p className="text-xs text-gray-400 line-through">
                                            {restaurantSettings?.currency_symbol || 'SAR'} {originalTotal.toFixed(2)}
                                          </p>
                                        </>
                                      )
                                    })()}
                                  </div>
                                ) : item.isComboItem && item.originalPrice ? (
                                  <div>
                                    <p className="text-sm font-bold text-green-600 dark:text-green-400">
                                      {restaurantSettings?.currency_symbol || 'SAR'} {((item.comboPrice || item.finalPrice) * item.quantity).toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-400 line-through">
                                      {restaurantSettings?.currency_symbol || 'SAR'} {(item.originalPrice * item.quantity).toFixed(2)}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {restaurantSettings?.currency_symbol || 'SAR'} {(item.finalPrice * item.quantity).toFixed(2)}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Priority: {item.priority}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {/* Applied Offers - in scroll area so footer stays short */}
                  {appliedOffers.length > 0 && (
                    <div className="space-y-2 pt-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Applied Offers
                      </h4>
                      <div className="space-y-2">
                        {appliedOffers.map(offer => (
                          <div key={offer.id} className="flex items-center justify-between bg-green-50 dark:bg-green-900 rounded-md p-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                                {offer.name}
                              </p>
                              {offer.description && (
                                <p className="text-xs text-green-600 dark:text-green-400 truncate">
                                  {offer.description}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => removeOffer(offer.id)}
                              className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                              title="Remove offer"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Offers Button */}
                  {getApplicableOffers().length > 0 && (
                    <button
                      onClick={() => setShowOffersModal(true)}
                      className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      <SparklesIcon className="h-5 w-5" />
                      <span>Available Offers ({getApplicableOffers().length})</span>
                    </button>
                  )}
                </div>

                {/* Cart Footer - collapse/expand summary + Place Order; default collapsed */}
                <div className="border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => setCartFooterExpanded(prev => !prev)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Order summary</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {restaurantSettings?.currency_symbol || 'SAR'} {getTotal().toFixed(2)}
                    </span>
                    {cartFooterExpanded ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500 shrink-0 ml-2" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500 shrink-0 ml-2" />
                    )}
                  </button>
                  {!cartFooterExpanded && (
                    <div className="px-4 pb-4">
                      <button
                        onClick={canEditOrder(selectedTable?.currentOrder) ? handleUpdateDraftOrder : handlePlaceOrder}
                        disabled={isPlacingOrder || cart.length === 0}
                        className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                      >
                        {isPlacingOrder 
                          ? 'Processing...' 
                          : canEditOrder(selectedTable?.currentOrder)
                            ? 'Update Order'
                            : selectedTable.currentOrder 
                              ? 'Add to Order' 
                              : 'Place Order'}
                      </button>
                    </div>
                  )}
                  {cartFooterExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div>
                        <textarea
                          value={orderNotes}
                          onChange={(e) => setOrderNotes(e.target.value)}
                          placeholder="Order notes..."
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none"
                          rows={2}
                        />
                      </div>
                      <div className="space-y-1">
                        {lastOrderToken !== null && (
                          <div className="flex justify-between items-center text-xs text-gray-700 dark:text-gray-300">
                            <span>Last Token:</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-semibold">
                              #{lastOrderToken}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                          <span>Subtotal:</span>
                          <span>{restaurantSettings?.currency_symbol || 'SAR'} {getSubtotal().toFixed(2)}</span>
                        </div>
                        {getDiscountAmount() > 0 && !appliedOffers.some(offer => offer.type === 'percentage' || offer.type === 'fixed') && (
                          <div className="flex justify-between text-sm text-red-600 dark:text-red-400">
                            <span>Discount:</span>
                            <span>-{restaurantSettings?.currency_symbol || 'SAR'} {getDiscountAmount().toFixed(2)}</span>
                          </div>
                        )}
                        {getTax() > 0 && (
                          <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                            <span>VAT ({restaurantSettings?.tax_rate || 15}% incl.):</span>
                            <span>{restaurantSettings?.currency_symbol || 'SAR'} {getTax().toFixed(2)}</span>
                          </div>
                        )}
                        {getServiceCharge() > 0 && (
                          <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                            <span>Service Charge ({restaurantSettings?.service_charge || 0}%):</span>
                            <span>{restaurantSettings?.currency_symbol || 'SAR'} {getServiceCharge().toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                          <span>Total:</span>
                          <span>{restaurantSettings?.currency_symbol || 'SAR'} {getTotal().toFixed(2)}</span>
                        </div>
                      </div>
                      <button
                        onClick={canEditOrder(selectedTable?.currentOrder) ? handleUpdateDraftOrder : handlePlaceOrder}
                        disabled={isPlacingOrder || cart.length === 0}
                        className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                      >
                        {isPlacingOrder 
                          ? 'Processing...' 
                          : canEditOrder(selectedTable?.currentOrder)
                            ? 'Update Order'
                            : selectedTable.currentOrder 
                              ? 'Add to Order' 
                              : 'Place Order'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Customization Modal - inside fullscreen container so it shows in fullscreen */}
            {showCustomizationModal && selectedProduct && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedProduct.name}
                    </h3>
                    <button
                      onClick={() => setShowCustomizationModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Variant
                      </label>
                      <div className="space-y-2">
                        {selectedProduct.variants.map(variant => (
                          <button
                            key={variant.id}
                            onClick={() => setSelectedVariant(variant)}
                            className={`w-full text-left p-3 rounded-lg border-2 ${
                              selectedVariant?.id === variant.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900'
                                : 'border-gray-200 dark:border-gray-600'
                            }`}
                          >
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-900 dark:text-white">{variant.name}</span>
                              <span className="text-primary-600 dark:text-primary-400">
                                +{restaurantSettings?.currency_symbol || 'SAR'} {variant.priceModifier.toFixed(2)}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedProduct.addons && selectedProduct.addons.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Addons
                      </label>
                      <div className="space-y-2">
                        {selectedProduct.addons.map(addon => (
                          <label
                            key={addon.id}
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedAddons.some(a => a.id === addon.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAddons([...selectedAddons, addon])
                                  } else {
                                    setSelectedAddons(selectedAddons.filter(a => a.id !== addon.id))
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-gray-900 dark:text-white">{addon.name}</span>
                            </div>
                            <span className="text-primary-600 dark:text-primary-400">
                              +{restaurantSettings?.currency_symbol || 'SAR'} {addon.price.toFixed(2)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={itemNotes}
                      onChange={(e) => setItemNotes(e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={3}
                      placeholder="Special instructions..."
                    />
                  </div>

                  <button
                    onClick={handleConfirmAddToCart}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            )}
          </>

      {/* Table Settings Modal - inside fullscreen container */}
      {showTableSettingsModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Table Settings - {selectedTable.number}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number of Customers
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCustomerCount(Math.max(0, customerCount - 1))}
                    className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                    type="button"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </button>
                  <div className="flex-1 text-center">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{customerCount}</span>
                  </div>
                  <button
                    onClick={() => setCustomerCount(customerCount + 1)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                    type="button"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Additional Chairs
                </label>
                <input
                  type="number"
                  min="0"
                  value={additionalChairs}
                  onChange={(e) => setAdditionalChairs(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Merged Tables Section */}
              {selectedTable.mergedTables && selectedTable.mergedTables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Merged Tables
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
                    {selectedTable.mergedTables.map((mergedTableId: string) => {
                      const mergedTable = tables.find(t => t.id === mergedTableId)
                      return mergedTable ? (
                        <div
                          key={mergedTableId}
                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                        >
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              Table {mergedTable.number}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {mergedTable.location} • Capacity: {mergedTable.capacity}
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              const updatedMergedTables = selectedTable.mergedTables?.filter(id => id !== mergedTableId) || []
                              try {
                                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
                                const response = await fetch(`${apiUrl}/tables/${selectedTable.id}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ merged_tables: updatedMergedTables })
                                })
                                const data = await response.json()
                                if (data.success) {
                                  setToast({ message: 'Table unmerged successfully', type: 'success' })
                                  await loadTables()
                                  // Update selected table state
                                  const updatedTable = tables.find(t => t.id === selectedTable.id)
                                  if (updatedTable) {
                                    setSelectedTable({ ...updatedTable, mergedTables: updatedMergedTables })
                                  }
                                } else {
                                  setToast({ message: data.error || 'Failed to unmerge table', type: 'error' })
                                }
                              } catch (error) {
                                console.error('Error unmerging table:', error)
                                setToast({ message: 'Failed to unmerge table', type: 'error' })
                              }
                            }}
                            className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800"
                          >
                            Unmerge
                          </button>
                        </div>
                      ) : null
                    })}
                  </div>
                </div>
              )}

              <div>
                <button
                  onClick={() => {
                    setShowMergeTablesModal(true)
                    setShowTableSettingsModal(false)
                  }}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  <LinkIcon className="h-4 w-4 inline mr-2" />
                  {selectedTable.mergedTables && selectedTable.mergedTables.length > 0 ? 'Add More Tables' : 'Merge Tables'}
                </button>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowTableSettingsModal(false)
                  setAdditionalChairs(0)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTableSettings}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Order Modal - inside fullscreen container */}
      {showTransferOrderModal && selectedTable && selectedTable.currentOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Transfer Order to Another Table
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>Current Table: <span className="font-medium">{selectedTable.number}</span></p>
                <p>{selectedTable.currentOrder.orderNumber || `ORD-${selectedTable.currentOrder.id.slice(0, 8).toUpperCase()}`}</p>
              </div>
            </div>

            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Floor Plan Filter */}
                <select
                  value={transferModalFloorPlanId}
                  onChange={(e) => setTransferModalFloorPlanId(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Floors</option>
                  {floorPlans.map(fp => (
                    <option key={fp.id} value={fp.id}>
                      {fp.name}
                    </option>
                  ))}
                </select>

                {/* Table Search */}
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tables..."
                    value={transferTableSearchTerm}
                    onChange={(e) => setTransferTableSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Table Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {(() => {
                const filteredTransferTables = tables
                  .filter(t => t.id !== selectedTable.id)
                  .filter(t => {
                    if (transferModalFloorPlanId !== 'all' && t.floor_plan_id !== transferModalFloorPlanId) {
                      return false
                    }
                    if (transferTableSearchTerm) {
                      return t.number.toLowerCase().includes(transferTableSearchTerm.toLowerCase())
                    }
                    return true
                  })

                if (filteredTransferTables.length === 0) {
                  return (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                      No tables found
                    </div>
                  )
                }

                return (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {filteredTransferTables.map(table => (
                      <div
                        key={table.id}
                        onClick={() => setTargetTableId(table.id)}
                        className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                          targetTableId === table.id
                            ? 'bg-primary-100 dark:bg-primary-900 border-primary-500 scale-105'
                            : 'bg-gray-50 dark:bg-gray-700 border-transparent hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                            {table.number}
                          </h4>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            table.status === 'available' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            table.status === 'occupied' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            table.status === 'reserved' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            table.status === 'waiting_for_approval' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                            'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                            {table.status === 'waiting_for_approval' ? 'W' : table.status.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          <div className="flex items-center gap-1">
                            <UserGroupIcon className="h-3 w-3" />
                            <span>Cap: {table.capacity}</span>
                          </div>
                          {table.customerCount && table.customerCount > 0 && (
                            <div className="text-xs font-medium text-primary-600 dark:text-primary-400">
                              {table.customerCount} guests
                            </div>
                          )}
                          {table.floor_plan_id && (
                            <div className="text-xs text-gray-500">
                              {getFloorPlanName(table.floor_plan_id)}
                            </div>
                          )}
                          {table.currentOrder && (
                            <div className="text-xs font-medium text-orange-600 dark:text-orange-400">
                              Has Order
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>

            {/* Footer with Warning and Buttons */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
              {targetTableId && (
                <div className="space-y-2">
                  {(() => {
                    const targetTable = tables.find(t => t.id === targetTableId)
                    // Use current state values (customerCount, additionalChairs) instead of selectedTable
                    // These are updated when user changes them in table settings
                    const currentGuestCount = customerCount || 0
                    // const currentAdditionalChairs = additionalChairs || 0
                    const targetCapacity = targetTable?.capacity || 0
                    // const targetCurrentAdditionalChairs = targetTable?.additionalChairs || 0
                    
                    // Calculate additional chairs needed
                    // If guest count > target capacity, need extra chairs
                    // We don't transfer old table's additional chairs - we only calculate
                    // what's needed for the new table based on guest count
                    let additionalChairsNeeded = 0
                    if (currentGuestCount > targetCapacity) {
                      additionalChairsNeeded = currentGuestCount - targetCapacity
                    }
                    
                    // Total additional chairs = exactly what's needed for guest count
                    // We replace any existing additional chairs on the new table
                    // const totalAdditionalChairs = additionalChairsNeeded
                    
                    return (
                      <>
                        <div className="p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                          <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
                            Transfer Details:
                          </p>
                          <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                            <li>• Guest count ({currentGuestCount}) will be transferred</li>
                            <li>• Target table capacity: {targetCapacity}</li>
                            {additionalChairsNeeded > 0 && (
                              <li className="font-medium text-orange-600 dark:text-orange-400">
                                • {additionalChairsNeeded} additional chair(s) will be set (to accommodate {currentGuestCount} guests)
                              </li>
                            )}
                            {additionalChairsNeeded === 0 && currentGuestCount <= targetCapacity && (
                              <li className="text-green-600 dark:text-green-400">
                                • No additional chairs needed (guests fit within table capacity)
                              </li>
                            )}
                            {targetTable?.currentOrder && (
                              <li className="font-medium text-yellow-600 dark:text-yellow-400">
                                ⚠️ Table {targetTable.number} already has an order
                              </li>
                            )}
                          </ul>
                        </div>
                        {targetTable?.currentOrder && (
                          <div className="p-3 bg-yellow-50 dark:bg-yellow-900 rounded-lg">
                            <p className="text-sm text-yellow-800 dark:text-yellow-200">
                              The existing order will remain, and this order will be transferred.
                            </p>
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowTransferOrderModal(false)
                    setTargetTableId('')
                    setTransferModalFloorPlanId('all')
                    setTransferTableSearchTerm('')
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-3 px-4 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransferOrder}
                  disabled={!targetTableId || isTransferringOrder}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg"
                >
                  {isTransferringOrder ? 'Transferring...' : 'Transfer Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offers Modal - inside fullscreen container */}
      {showOffersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Available Offers
              </h3>
              <button
                onClick={() => setShowOffersModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {getApplicableOffers().length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No offers available for current cart
                </div>
              ) : (
                getApplicableOffers().map(offer => {
                  const applicableProducts = products.filter(product => 
                    offer.applicableItems.includes(product.id)
                  )
                  
                  return (
                    <div key={offer.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">{offer.name}</h4>
                          {offer.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{offer.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            applyOffer(offer)
                          }}
                          className="text-blue-600 hover:text-blue-500 text-sm font-medium ml-2"
                        >
                          Apply
                        </button>
                      </div>
                      
                      {/* Applicable Products */}
                      {applicableProducts.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Applicable Products:</p>
                          <div className="space-y-1">
                            {applicableProducts.map(product => (
                              <div key={product.id} className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 dark:text-gray-400">{product.name}</span>
                                <span className="text-green-600 dark:text-green-400 font-medium">
                                  {restaurantSettings?.currency_symbol || 'SAR'} {product.basePrice.toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Offer Details */}
                      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                        {offer.minOrderAmount && (
                          <div>Min order: {restaurantSettings?.currency_symbol || 'SAR'} {offer.minOrderAmount.toFixed(2)}</div>
                        )}
                        {offer.maxDiscount && (
                          <div>Max discount: {restaurantSettings?.currency_symbol || 'SAR'} {offer.maxDiscount.toFixed(2)}</div>
                        )}
                        {offer.type === 'percentage' && (
                          <div>Discount: {offer.value}% off</div>
                        )}
                        {offer.type === 'fixed' && (
                          <div>Discount: {restaurantSettings?.currency_symbol || 'SAR'} {offer.value.toFixed(2)} off</div>
                        )}
                        {offer.type === 'combo' && offer.comboItems && offer.comboItems.length > 0 && (
                          <div>Combo deal: {restaurantSettings?.currency_symbol || 'SAR'} {offer.comboItems.reduce((sum: number, item: any) => sum + (item.price || 0), 0).toFixed(2)}</div>
                        )}
                        {offer.type === 'bogo' && offer.bogoItems && offer.bogoItems.length > 0 && (
                          <div>Buy One Get One: {offer.bogoItems[0].discountType === 'free' ? 'Free' : `${offer.bogoItems[0].discountValue}% off`}</div>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowOffersModal(false)}
                className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal - inside fullscreen container */}
      {showOrderDetailsModal && selectedTable && selectedTable.currentOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Order Details
                </h3>
                <button
                  onClick={() => setShowOrderDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Order Header */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Order Number</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedTable.currentOrder.orderNumber || `ORD-${selectedTable.currentOrder.id.slice(0, 8).toUpperCase()}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                    selectedTable.currentOrder.status === 'waiting_for_approval' || selectedTable.currentOrder.status === 'draft'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      : selectedTable.currentOrder.status === 'modification_requested'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      : selectedTable.currentOrder.status === 'cancellation_requested'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : selectedTable.currentOrder.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    selectedTable.currentOrder.status === 'confirmed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    selectedTable.currentOrder.status === 'preparing' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    selectedTable.currentOrder.status === 'ready' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}>
                    {selectedTable.currentOrder.status === 'waiting_for_approval' || selectedTable.currentOrder.status === 'draft'
                      ? 'Waiting for Approval'
                      : selectedTable.currentOrder.status === 'modification_requested'
                      ? 'Modification Requested'
                      : selectedTable.currentOrder.status === 'cancellation_requested'
                      ? 'Cancellation Requested'
                      : selectedTable.currentOrder.status.charAt(0).toUpperCase() + selectedTable.currentOrder.status.slice(1)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Table</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {selectedTable.number}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Customer</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {selectedTable.currentOrder.customerName || 'Guest'}
                  </p>
                </div>
                {selectedTable.currentOrder.customerPhone && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Phone</p>
                    <p className="text-lg font-medium text-gray-900 dark:text-white">
                      {selectedTable.currentOrder.customerPhone}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Date</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {new Date(selectedTable.currentOrder.createdAt).toLocaleString()}
                  </p>
                </div>
                {selectedTable.currentOrder.paymentMethod && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
                    <p className="text-lg font-medium text-gray-900 dark:text-white capitalize">
                      {selectedTable.currentOrder.paymentMethod.replace('_', ' ')}
                    </p>
                  </div>
                )}
                {selectedTable.currentOrder.paymentStatus && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Payment Status</p>
                    <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                      selectedTable.currentOrder.paymentStatus === 'paid'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : selectedTable.currentOrder.paymentStatus === 'pending'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : selectedTable.currentOrder.paymentStatus === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                      {selectedTable.currentOrder.paymentStatus === 'paid' && '✓ '}
                      <span className="capitalize">{selectedTable.currentOrder.paymentStatus}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Order Items - use per-line list so quantities match what was ordered (not summed for KDS) */}
              {(() => {
                const displayItems = selectedTable.currentOrder.itemsForModification ?? selectedTable.currentOrder.items
                return (
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                  Items ({displayItems.length})
                </h4>
                <div className="space-y-2">
                  {displayItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formatItemName(item)}
                            </p>
                            {item.priority && (
                              <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-xs rounded">
                                Priority: {item.priority}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              item.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              item.status === 'preparing' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                              item.status === 'ready' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Quantity: {item.quantity} × {restaurantSettings?.currency_symbol || 'SAR'} {item.unitPrice.toFixed(2)}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {restaurantSettings?.currency_symbol || 'SAR'} {item.totalPrice.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
                )
              })()}

              {/* Order Calculations — computed from displayed items so totals match the list */}
              {(() => {
                const displayItems = selectedTable.currentOrder.itemsForModification ?? selectedTable.currentOrder.items
                const itemsTotal = displayItems.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0)
                const discount = Number(selectedTable.currentOrder.discountAmount) || 0
                const afterDiscount = Math.max(0, itemsTotal - discount)
                const taxRateDecimal = (Number(restaurantSettings?.tax_rate) || 15) / 100
                const { vat } = splitVatInclusive(afterDiscount, taxRateDecimal)
                const serviceCharge = Number(selectedTable.currentOrder.serviceCharge) || 0
                const total = afterDiscount + serviceCharge
                const symbol = restaurantSettings?.currency_symbol || 'SAR'
                const taxRatePct = restaurantSettings?.tax_rate ?? 15
                const servicePct = restaurantSettings?.service_charge ?? 0
                return (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                  <span>Subtotal:</span>
                  <span>{symbol} {itemsTotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600 dark:text-red-400">
                    <span>Discount:</span>
                    <span>-{symbol} {discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                  <span>VAT ({taxRatePct}% incl.):</span>
                  <span>{symbol} {vat.toFixed(2)}</span>
                </div>
                {serviceCharge > 0 && (
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                    <span>Service Charge ({servicePct}%):</span>
                    <span>{symbol} {serviceCharge.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">Total</p>
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {symbol} {total.toFixed(2)}
                  </p>
                </div>
              </div>
                )
              })()}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowOrderDetailsModal(false)}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Tables Modal - inside fullscreen container */}
      {showMergeTablesModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Merge Tables with {selectedTable.number}
            </h3>
            
            <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
              {tables
                .filter(t => t.id !== selectedTable.id && !selectedTable.mergedTables?.includes(t.id))
                .map(table => (
                  <label
                    key={table.id}
                    className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={tablesToMerge.includes(table.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTablesToMerge([...tablesToMerge, table.id])
                        } else {
                          setTablesToMerge(tablesToMerge.filter(id => id !== table.id))
                        }
                      }}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{table.number}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {table.location} • Capacity: {table.capacity}
                      </div>
                    </div>
                  </label>
                ))}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowMergeTablesModal(false)
                  setTablesToMerge([])
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleMergeTables}
                disabled={tablesToMerge.length === 0}
                className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg"
              >
                Merge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan customer order QR modal - live camera - inside fullscreen container */}
      {showScanQRModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden w-full max-w-md flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <QrCodeIcon className="h-6 w-6" />
                Scan QR code
              </h3>
              <button
                onClick={() => { setShowScanQRModal(false); setScanQRError(null) }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 px-4 pt-2">
              Point the camera at the customer&apos;s order QR code. The cart will fill automatically.
            </p>
            <div className="relative bg-black aspect-square max-h-[60vh] w-full flex items-center justify-center">
              <video
                ref={scanQRVideoRef}
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {scanQRError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white p-4 text-center">
                  <p className="text-sm mb-3">{scanQRError}</p>
                  <button
                    type="button"
                    onClick={() => scanQRInputRef.current?.click()}
                    className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium"
                  >
                    Choose image instead
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-2 p-4">
              <button
                type="button"
                onClick={() => { setShowScanQRModal(false); setScanQRError(null) }}
                className="flex-1 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 font-medium"
              >
                Cancel
              </button>
              {!scanQRError && (
                <button
                  type="button"
                  onClick={() => scanQRInputRef.current?.click()}
                  className="py-2 px-4 rounded-lg border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                >
                  Upload image
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modification Request Modal - inside fullscreen container */}
      {showModificationRequestModal && selectedTable?.currentOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Request Order Modification
                </h3>
                <button
                  onClick={() => {
                    setShowModificationRequestModal(false)
                    setModificationReason('')
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Current order items have been loaded into the cart. You can add, remove, or modify items below.
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Current Order Items: {selectedTable.currentOrder.items.length}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Items in Cart: {cart.length}
                  </p>
                </div>
                {cart.length === 0 && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      ⚠️ Cart is empty. Please add items to the cart to request modifications.
                    </p>
                  </div>
                )}
              </div>

              {/* Show current cart items */}
              {cart.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Items to Modify ({cart.length})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatItemName(item)} x{item.quantity}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {restaurantSettings?.currency_symbol || 'SAR'} {item.finalPrice.toFixed(2)} each
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const newCart = cart.map(cartItem => 
                                cartItem.id === item.id 
                                  ? { ...cartItem, quantity: Math.max(1, cartItem.quantity - 1) }
                                  : cartItem
                              )
                              setCart(newCart)
                            }}
                            className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            -
                          </button>
                          <span className="text-sm font-medium text-gray-900 dark:text-white w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => {
                              setCart(cart.filter(cartItem => cartItem.id !== item.id))
                            }}
                            className="ml-2 p-1 text-red-600 hover:text-red-700"
                            title="Remove item"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={modificationReason}
                  onChange={(e) => setModificationReason(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Explain the reason for modification..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowModificationRequestModal(false)
                    setModificationReason('')
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestModification}
                  disabled={isSubmittingRequest || cart.length === 0}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {isSubmittingRequest ? 'Submitting...' : cart.length === 0 ? 'Add Items First' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Request Modal - inside fullscreen container */}
      {showCancellationRequestModal && selectedTable?.currentOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Request Order Cancellation
                </h3>
                <button
                  onClick={() => {
                    setShowCancellationRequestModal(false)
                    setCancellationReason('')
                    setCancellationReasonDetails('')
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cancellation Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a reason</option>
                  <option value="customer_mind_change">Customer Mind Change</option>
                  <option value="delay_in_order">Delay in Order</option>
                  <option value="issue_in_food">Issue in Food</option>
                  <option value="issue_with_waiter">Issue with Waiter</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  value={cancellationReasonDetails}
                  onChange={(e) => setCancellationReasonDetails(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Provide additional details..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCancellationRequestModal(false)
                    setCancellationReason('')
                    setCancellationReasonDetails('')
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestCancellation}
                  disabled={isSubmittingRequest || !cancellationReason}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {isSubmittingRequest ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast - inside fullscreen container */}
      {toast && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-[110] ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Confirm Modal - inside fullscreen container */}
      {showConfirmModal && confirmModalConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Confirm Action
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {confirmModalConfig.message}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false)
                    setConfirmModalConfig(null)
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmModalConfig.onConfirm()
                    setConfirmModalConfig(null)
                  }}
                  className={`px-4 py-2 text-white rounded-md text-sm font-medium ${
                    confirmModalConfig.confirmButtonColor === 'green'
                      ? 'bg-green-600 hover:bg-green-700'
                      : confirmModalConfig.confirmButtonColor === 'blue'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {confirmModalConfig.confirmButtonText || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      )}
      </div>

      {/* Toast when no table selected (table list view) */}
      {!selectedTable && toast && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-[110] ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  )
}

export default WaiterModule
